import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Flame, Plus, Menu as MenuIcon, Sparkles, ArrowDown } from 'lucide-react';
import { sdk } from '@farcaster/miniapp-sdk';
import { CardFan } from './components/CardFan';
import { SelectedCards } from './components/SelectedCards';
import { CardModal } from './components/CardModal';
import { ShareModal } from './components/ShareModal';
import { TestPanel } from './components/TestPanel';
import { Background } from './components/Background';
import { OnboardingModal } from './components/OnboardingModal';
import { QuestionField } from './components/QuestionField';
import { MaintenanceModal } from './components/MaintenanceModal';
import { AdminSettingsModal } from './components/AdminSettingsModal';
import { MenuModal } from './components/MenuModal';
import { InfoModal } from './components/InfoModal';
import { InterpretationModal } from './components/InterpretationModal';
import { InterpretationResultModal } from './components/InterpretationResultModal';
import { generateDailyCards, getCardById } from './utils/cardUtils';
import { DeckKey, TarotCardData } from './types/tarot';
import { getDeckDefinition } from './data/decks';
import { getDailyReading, saveDailyReading, canDrawToday, getStreak, getInitialStreak, updateStreak, getDaysSinceLastReadingFromDate } from './utils/storage';
import { preloadImages } from './utils/imagePreloader';
import { extractReferralData, saveReferralData, processReferral } from './utils/referrals';
import { generateCardMetaTags, updateMetaTags } from './utils/metaTags';
import { initializeErrorSuppression } from './utils/errorSuppression';
// Add Supabase imports
import { createOrUpdateUser, saveReading, updateUserStreak, resetUserReadings } from './utils/supabaseUtils';
import { requestInterpretation, fetchInterpretationUsage, InterpretationError } from './services/interpretationService';
import { createInterpretationPayment, confirmInterpretationPayment, PaymentCancelledError, PaymentTimeoutError, PaymentError } from './services/paymentService';
import { supabase } from './config/supabase';
import { useMiniAppAuth } from './hooks/useMiniAppAuth';
import { useAdminPermissions, useMaintenanceMode, useSetting } from './hooks/useSettings';
import { log } from './utils/debugLogger';
import { formatEther } from 'viem';
import ShareArrowIcon from './components/ShareArrowIcon';
import { ReadingHistoryModal, ReadingHistoryEntry } from './components/ReadingHistoryModal';
import { DeckToggle } from './components/DeckToggle';
import { FARCASTER_DEEP_LINK } from './utils/farcasterShare';
import { FarcasterUser } from './types/farcaster';

const LOCAL_ACCESS_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);
const BASE_APP_REFERRER_HINTS = ['base.app', 'base.xyz', 'coinbase.com', 'wallet.coinbase.com'];

const isLocalNetworkHost = (hostname: string) => {
  if (!hostname) return false;
  if (LOCAL_ACCESS_HOSTS.has(hostname)) return true;
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    if (hostname.startsWith('10.') || hostname.startsWith('192.168.') || hostname.startsWith('127.') || hostname.startsWith('0.0.')) {
      return true;
    }
    const secondOctet = Number.parseInt(hostname.split('.')[1], 10);
    if (hostname.startsWith('172.') && secondOctet >= 16 && secondOctet <= 31) {
      return true;
    }
  }
  if (hostname.endsWith('.local')) {
    return true;
  }
  return false;
};

function useFarcasterOnly() {
  return 'allowed';
}

const isBaseAppReferrer = (value: string) => {
  if (!value) return false;
  const normalized = value.toLowerCase();
  return BASE_APP_REFERRER_HINTS.some((hint) => normalized.includes(hint));
};

const inferReferrerDomain = (referrer: string) => {
  if (!referrer) return '';
  try {
    return new URL(referrer).hostname;
  } catch {
    return referrer;
  }
};

const MOCK_READING_CARD_SETS: string[][] = [
  ['01001', '01005', '01012'],
  ['01007', '01009', '01015'],
  ['01018', '01021', '01025'],
  ['01028', '01030', '01034'],
  ['01036', '01038', '01040'],
  ['01042', '01045', '01047'],
  ['01049', '01050', '01053'],
  ['01055', '01059', '01061'],
  ['01063', '01065', '01067'],
  ['01069', '01071', '01073'],
];

const MOCK_QUESTIONS = [
  'What energy should I focus on before launching the new feature?',
  'How can I rebuild momentum after a rough market week?',
  'Which collaboration feels most aligned right now?',
  'Where should I direct my learning to level up as a founder?',
  'How do I keep the community motivated this month?',
  'What should I let go of to get unstuck?',
];

const MOCK_INSIGHTS = [
  'A quiet breakthrough is brewing—nurture it with consistency and it will soon surface.',
  'Momentum returns when you detach from the outcome and enjoy the experiment again.',
  'Your intuition already knows the answer. Give it space to speak and then act swiftly.',
  'Not every risk requires a leap; sometimes the next right step is a steady shuffle forward.',
];

type PendingSavePayload = {
  cards: TarotCardData[];
  question: string;
  readingType: 'daily' | 'additional';
  deckKey?: DeckKey;
};

const cloneCardWithOrientation = (card: TarotCardData, orientation: 'upright' | 'reversed'): TarotCardData => {
  if (orientation === 'upright') {
    return {
      ...card,
      orientation: 'upright',
      meaning: card.uprightMeaning,
      cryptoInsight: card.cryptoInsight ?? card.uprightMeaning,
    };
  }

  return {
    ...card,
    orientation: 'reversed',
    meaning: card.reversedMeaning,
    cryptoInsight: card.reversedCryptoInsight ?? card.reversedMeaning,
  };
};

const createMockReadingHistory = (): ReadingHistoryEntry[] => {
  const totalEntries = 24;
  const now = Date.now();

  return Array.from({ length: totalEntries }).map((_, index) => {
    const cardSet = MOCK_READING_CARD_SETS[index % MOCK_READING_CARD_SETS.length];
    const cards: TarotCardData[] = cardSet
      .map((cardId, cardPosition) => {
        const base = getCardById(cardId);
        if (!base) return null;
        const isReversed = (index + cardPosition) % 3 === 0;
        return cloneCardWithOrientation(base, isReversed ? 'reversed' : 'upright');
      })
      .filter((card): card is TarotCardData => Boolean(card));

    const createdAt = new Date(now - index * 12 * 60 * 60 * 1000).toISOString();
    const question = index % 2 === 0 ? MOCK_QUESTIONS[index % MOCK_QUESTIONS.length] : null;
    const insight = index % 3 === 0 ? MOCK_INSIGHTS[index % MOCK_INSIGHTS.length] : null;

    return {
      id: `mock-reading-${index + 1}`,
      createdAt,
      readingType: index % 3 === 0 ? 'additional' : 'daily',
      cards,
      deckKey: (cards[0]?.deckKey as DeckKey) ?? 'crypto',
      question,
      insight,
      shareUrl: insight ? `https://warpcast.com/~/compose?text=${encodeURIComponent(insight)}` : null,
    } satisfies ReadingHistoryEntry;
  });
};


function App() {
  const allowBrowserAccess = true;
  const supabaseDisabled = import.meta.env.VITE_DISABLE_SUPABASE === 'true';
  const enableStandardDeck = import.meta.env.VITE_ENABLE_STANDARD_DECK === 'true';
  const interpretationPriceUsd = Number.parseFloat(import.meta.env.VITE_INTERPRETATION_PRICE_USD ?? '0.15');
  const interpretationPriceEthInput = import.meta.env.VITE_INTERPRETATION_PRICE_ETH?.trim();
  const normalizedInterpretationPriceEth = interpretationPriceEthInput
    ? interpretationPriceEthInput.replace(',', '.')
    : '';
  const hasManualEthPrice = normalizedInterpretationPriceEth !== ''
    && Number.isFinite(Number.parseFloat(normalizedInterpretationPriceEth))
    && Number.parseFloat(normalizedInterpretationPriceEth) > 0;
  const interpretationPriceEthString = hasManualEthPrice ? normalizedInterpretationPriceEth : '';
  const interpretationPriceEth = hasManualEthPrice
    ? Number.parseFloat(interpretationPriceEthString)
    : Number.NaN;
  const interpretationPriceWei = import.meta.env.VITE_INTERPRETATION_PRICE_WEI || null;
  const interpretationDailyLimit = Number.parseInt(import.meta.env.VITE_INTERPRETATION_LIMIT ?? '10', 10) || 10;
  const interpretationFlagEnabled = import.meta.env.VITE_ENABLE_INTERPRETATION === 'true';
  const mockInterpretationEnabled = import.meta.env.VITE_MOCK_INTERPRETATION === 'true';
  const forceInterpretationPaymentError = import.meta.env.VITE_FORCE_INTERPRETATION_PAYMENT_ERROR === 'true';
  const interpretationLabel = useMemo(
    () => (import.meta.env.PROD ? 'Magic Insight' : 'Cosmic Insight'),
    [],
  );

  const MINIAPP_AUTO_PROMPT_KEY = 'miniapp_auto_prompted';
  const MINIAPP_ADDED_STORAGE_KEY = 'miniapp_added_flag';
  const ACTIVE_DECK_STORAGE_KEY = 'active_deck';
  const USE_LEGACY_ONBOARDING = false;
  const DEFAULT_READING_LIMITS = {
    canDoDaily: true,
    canDoAdditional: true,
    stats: {
      dailyUsed: 0,
      dailyLimit: 1,
      dailyRemaining: 1,
      additionalUsed: 0,
      maxAdditional: 2,
      additionalRemaining: 2,
    },
  } as const;

  const hydrateCard = useCallback(
    (rawCard: any): TarotCardData => {
      const rawId = typeof rawCard?.id === 'string' ? rawCard.id : '';
      const baseCard = rawId ? getCardById(rawId) : undefined;
      const deckKey = (rawCard?.deckKey ?? baseCard?.deckKey ?? 'crypto') as DeckKey;
      const deck = getDeckDefinition(deckKey);

      const id = rawId || baseCard?.id || `${deck.deckId}000`;
      const image = rawCard?.image ?? baseCard?.image ?? '';
      const orientation = rawCard?.orientation === 'reversed' ? 'reversed' : 'upright';
      const uprightMeaning = rawCard?.uprightMeaning ?? baseCard?.uprightMeaning ?? rawCard?.meaning ?? baseCard?.meaning ?? 'Meaning coming soon.';
      const reversedMeaning = rawCard?.reversedMeaning ?? baseCard?.reversedMeaning ?? uprightMeaning;
      const uprightInsight = rawCard?.cryptoInsight ?? baseCard?.cryptoInsight ?? uprightMeaning;
      const reversedInsight = rawCard?.reversedCryptoInsight ?? baseCard?.reversedCryptoInsight ?? reversedMeaning;
      const meaning = orientation === 'reversed' ? reversedMeaning : uprightMeaning;
      const cryptoInsight = orientation === 'reversed' ? reversedInsight : uprightInsight;

      return {
        id,
        deckKey,
        name: rawCard?.name ?? baseCard?.name ?? 'Unknown card',
        image,
        backImage: rawCard?.backImage ?? baseCard?.backImage ?? deck.backImage,
        orientation,
        meaning,
        uprightMeaning,
        reversedMeaning,
        cryptoInsight,
        reversedCryptoInsight: reversedInsight,
      };
    },
    [],
  );

  const transformReadingHistory = useCallback(
    (rawReadings: any[]): ReadingHistoryEntry[] => {
      if (!Array.isArray(rawReadings)) {
        return [];
      }

      return rawReadings
        .map((reading) => {
          let rawCards = reading?.selected_cards ?? reading?.cards ?? [];

          if (typeof rawCards === 'string') {
            try {
              const parsed = JSON.parse(rawCards);
              rawCards = Array.isArray(parsed) ? parsed : [];
            } catch (error) {
              console.warn('Failed to parse selected_cards JSON:', error);
              rawCards = [];
            }
          }

          if (!Array.isArray(rawCards)) {
            rawCards = [];
          }

          const cards = rawCards
            .map((card) => hydrateCard(card))
            .filter((card) => card && card.image);

          if (cards.length === 0) {
            return null;
          }

          const deckKey = (reading?.deck_key ?? cards[0]?.deckKey ?? 'crypto') as DeckKey;
          const createdAt = reading?.created_at ?? reading?.createdAt ?? new Date().toISOString();

          return {
            id: reading?.id?.toString() ?? `${deckKey}-${createdAt}`,
            createdAt,
            readingType: reading?.reading_type === 'additional' ? 'additional' : 'daily',
            cards,
            deckKey,
            question: reading?.question_text ?? reading?.question ?? null,
            insight: reading?.insight ?? reading?.interpretation ?? null,
            shareUrl: reading?.share_url ?? reading?.shareUrl ?? null,
          } satisfies ReadingHistoryEntry;
        })
        .filter((entry): entry is ReadingHistoryEntry => entry !== null);
    },
    [hydrateCard],
  );

  const applyReadingHistory = useCallback(
    (rawReadings: any[]) => {
      const entries = transformReadingHistory(rawReadings);

      if (entries.length > 0) {
        setReadingHistory(entries);
        return;
      }

      if (allowBrowserAccess) {
        setReadingHistory(FALLBACK_READING_HISTORY);
      } else {
        setReadingHistory([]);
      }
    },
    [transformReadingHistory, allowBrowserAccess],
  );

  // GLOBAL API RATE LIMITING to prevent infinity loops
  const apiCallTracker = useRef<Record<string, number>>({});
  const apiResultsCache = useRef<Record<string, any>>({});
  const API_CALL_LIMIT = 2; // max calls per endpoint per 10 seconds
  const API_CALL_WINDOW = 10000; // 10 seconds
  
  const isApiCallAllowed = (endpoint: string): boolean => {
    const now = Date.now();
    const key = endpoint;
    
    if (!apiCallTracker.current[key]) {
      apiCallTracker.current[key] = now;
      return true;
    }
    
    const timeSinceLastCall = now - apiCallTracker.current[key];
    if (timeSinceLastCall < API_CALL_WINDOW) {
      if (import.meta.env.DEV) {
        console.log('🚫 API call BLOCKED for', endpoint, '- too many calls, wait', Math.round((API_CALL_WINDOW - timeSinceLastCall)/1000) + 's');
        console.log('🔍 CALL STACK (to find infinity loop source):', new Error().stack);
      }
      
      // Instead of hard blocking, return false so caller can use cached data
      return false;
    }
    
    apiCallTracker.current[key] = now;
    return true;
  };

  // SIMPLE 60-SECOND CACHE with TTL
  const getCachedResult = (endpoint: string) => {
    const cached = apiResultsCache.current[endpoint];
    if (!cached) return null;
    
    const now = Date.now();
    const CACHE_TTL_MS = 120000; // 120 seconds (2 minutes) - more aggressive caching for better performance
    
    if (cached.timestamp && (now - cached.timestamp) > CACHE_TTL_MS) {
      // Cache expired, remove it
      delete apiResultsCache.current[endpoint];
      if (import.meta.env.DEV) {
        console.log('🗑️ Cache EXPIRED for', endpoint);
      }
      return null;
    }
    
    if (import.meta.env.DEV) {
      console.log('✅ Cache HIT for', endpoint, '(age: ' + Math.round((now - cached.timestamp)/1000) + 's)');
    }
    return cached.data;
  };

  const setCachedResult = (endpoint: string, result: any) => {
    apiResultsCache.current[endpoint] = {
      data: result,
      timestamp: Date.now()
    };
    if (import.meta.env.DEV) {
      console.log('💾 Cache STORED for', endpoint);
    }
  };

  const promptAddMiniApp = useCallback(
    async (options?: { force?: boolean }) => {
      if (typeof window === 'undefined') {
        return;
      }

      if (allowBrowserAccess && !options?.force) {
        return;
      }

      try {
        const isMiniApp = await sdk.isInMiniApp();
        if (!isMiniApp) {
          return;
        }

        const context = await sdk.context;
        if (context?.client?.added) {
          window.localStorage.setItem(MINIAPP_ADDED_STORAGE_KEY, 'true');
          return;
        }

        if (!options?.force) {
          const markedAdded = window.localStorage.getItem(MINIAPP_ADDED_STORAGE_KEY) === 'true';
          if (markedAdded) {
            return;
          }

          const hasPrompted = window.localStorage.getItem(MINIAPP_AUTO_PROMPT_KEY) === 'true';
          if (hasPrompted) {
            return;
          }
        }

        await sdk.actions.addMiniApp();

        if (!options?.force) {
          window.localStorage.setItem(MINIAPP_AUTO_PROMPT_KEY, 'true');
        }
      } catch (error) {
        console.warn('Failed to prompt add mini app:', error);
      }
    },
    [allowBrowserAccess],
  );

  const handleManualAddMiniApp = useCallback(() => {
    void promptAddMiniApp({ force: true });
  }, [promptAddMiniApp]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const markAdded = () => window.localStorage.setItem(MINIAPP_ADDED_STORAGE_KEY, 'true');
    const clearAdded = () => window.localStorage.removeItem(MINIAPP_ADDED_STORAGE_KEY);
    const markPrompted = () => window.localStorage.setItem(MINIAPP_AUTO_PROMPT_KEY, 'true');

    sdk.on('miniAppAdded', markAdded);
    sdk.on('miniAppRemoved', clearAdded);
    sdk.on('miniAppAddRejected', markPrompted);

    return () => {
      sdk.off('miniAppAdded', markAdded);
      sdk.off('miniAppRemoved', clearAdded);
      sdk.off('miniAppAddRejected', markPrompted);
    };
  }, []);

  const [selectedCards, setSelectedCards] = useState<TarotCardData[]>([]);
  const [availableCards, setAvailableCards] = useState<TarotCardData[]>([]);
  const [selectedCardModal, setSelectedCardModal] = useState<TarotCardData | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [testPanelOpen, setTestPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Initialize streak count from localStorage, defaults to 0 for new users
  const [streakCount, setStreakCount] = useState(getStreak());
  const [canDraw, setCanDraw] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [readingType, setReadingType] = useState<'daily' | 'additional'>('daily');
  const [canDoAdditional, setCanDoAdditional] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const maxCards = 3;
  const [activeDeck, setActiveDeck] = useState<DeckKey>(() => {
    if (typeof window === 'undefined') return 'crypto';
    const stored = window.localStorage.getItem(ACTIVE_DECK_STORAGE_KEY);
    if (stored === 'standard' && enableStandardDeck) {
      return 'standard';
    }
    return 'crypto';
  });

  // Debug state for mobile debugging
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [showDebugPanel, setShowDebugPanel] = useState<boolean>(false);
  const [miniAppReferrer, setMiniAppReferrer] = useState<string | null>(null);
  const [isBaseApp, setIsBaseApp] = useState(false);
  
  // New states for proper flow management
  const [isReadingComplete, setIsReadingComplete] = useState(false);
  const [isReadingSaved, setIsReadingSaved] = useState(false);
  const [totalReadingsToday, setTotalReadingsToday] = useState(0);
  const [dailyRemaining, setDailyRemaining] = useState(0);
  const [additionalRemaining, setAdditionalRemaining] = useState(0);
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showFanHint, setShowFanHint] = useState(false);
  const [readingHistory, setReadingHistory] = useState<ReadingHistoryEntry[]>([]);
  const [readingHistoryLoading, setReadingHistoryLoading] = useState(false);

  const [interpretationState, setInterpretationState] = useState<'idle' | 'modal' | 'processing' | 'ready' | 'error'>('idle');
  const [interpretationText, setInterpretationText] = useState('');
  const [interpretationError, setInterpretationError] = useState<string | null>(null);
  const [interpretationsUsed, setInterpretationsUsed] = useState(0);
  const [serverInterpretationLimit, setServerInterpretationLimit] = useState<number | null>(null);
  const [serverInterpretationPrice, setServerInterpretationPrice] = useState<number | null>(null);
  const [serverInterpretationPriceWei, setServerInterpretationPriceWei] = useState<string | null>(null);
  const [shareContext, setShareContext] = useState<'reading' | 'insight'>('reading');
  const [shareInsightText, setShareInsightText] = useState('');
  const [shareUrlOverride, setShareUrlOverride] = useState<string | undefined>();
  
  // Performance optimization: cache validated limits
  const [preValidatedLimits, setPreValidatedLimits] = useState<any>(null);
  
  // Track component mount status to prevent setState after unmount
  const isMountedRef = useRef(true);
  const emptySlotTapRef = useRef<{ count: number; lastTap: number }>({ count: 0, lastTap: 0 });
  const lastSaveAttemptRef = useRef<PendingSavePayload | null>(null);
  const latestUserRef = useRef<FarcasterUser | null>(null);
  const latestAuthRef = useRef<boolean>(false);
  const autoRetryInFlightRef = useRef<boolean>(false);
  
  // Enhanced safe setState function to prevent React Error #300
  // Enhanced safe state update with comprehensive error protection
  const safeSetState = (setter, value) => {
    if (!isMountedRef.current) {
      console.log('🛡️ Prevented setState after unmount');
      return;
    }
    
    try {
      // Validate setter is a function
      if (typeof setter !== 'function') {
        console.error('❌ safeSetState: Invalid setter function:', typeof setter);
        return;
      }
      
      // Handle function values (setState with callback)
      if (typeof value === 'function') {
        setter((prevValue) => {
          try {
            const newValue = value(prevValue);
            return newValue;
          } catch (error) {
            console.error('❌ State callback error:', error);
            return prevValue; // Return previous value if callback fails
          }
        });
      } else {
        setter(value);
      }
    } catch (error) {
      console.error('❌ State update failed:', error);
      console.error('❌ State update error details:', { setter: setter?.name, value });
    }
  };

  const interpretationAllowedFids = useMemo(() => {
    const raw = import.meta.env.VITE_INTERPRETATION_ALLOWED_FIDS || '';
    return raw
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => Number.parseInt(entry, 10))
      .filter((entry) => !Number.isNaN(entry));
  }, []);

  const handleFanInteract = useCallback(() => {
    emptySlotTapRef.current = { count: 0, lastTap: 0 };
    if (showFanHint) {
      setShowFanHint(false);
    }
  }, [showFanHint]);

  const handleEmptySlotTap = useCallback(() => {
    const nextCount = emptySlotTapRef.current.count + 1;
    emptySlotTapRef.current = { count: nextCount, lastTap: Date.now() };
    if (nextCount >= 2) {
      setShowFanHint(true);
    }
  }, []);

  const handleSelectDeck = useCallback(
    (nextDeck: DeckKey) => {
      handleFanInteract();
      if (!enableStandardDeck && nextDeck === 'standard') {
        return;
      }

      const switchDisabled = isSaving || (isReadingComplete && !canDraw && !canDoAdditional);

      setActiveDeck((prev) => {
        if (prev === nextDeck || switchDisabled) {
          return prev;
        }

        const newCards = generateDailyCards(nextDeck);
        setSelectedCards([]);
        setSelectedCardModal(null);
        setIsReadingComplete(false);
        setIsReadingSaved(false);
        setShowActionButtons(false);
        setAvailableCards(newCards);
        preloadImages(newCards.map(card => card.image));
        return nextDeck;
      });
    },
    [handleFanInteract, enableStandardDeck, isSaving, isReadingComplete, canDraw, canDoAdditional],
  );

  const loadInterpretationUsage = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem('interpretation_usage');
      if (!stored) return;
      const parsed = JSON.parse(stored);
      const today = new Date().toISOString().split('T')[0];
      if (parsed?.date === today && typeof parsed?.count === 'number') {
        setInterpretationsUsed(parsed.count);
      }
    } catch (error) {
      console.warn('Failed to load interpretation usage:', error);
    }
  }, []);

  const persistInterpretationUsage = useCallback(
    (count: number) => {
      if (typeof window === 'undefined') return;
      const today = new Date().toISOString().split('T')[0];
      window.localStorage.setItem('interpretation_usage', JSON.stringify({ date: today, count }));
    },
    [],
  );

  const resetInterpretationUsageIfNeeded = useCallback(() => {
    if (typeof window === 'undefined') return;
    const today = new Date().toISOString().split('T')[0];
    const stored = window.localStorage.getItem('interpretation_usage');
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.date !== today) {
        persistInterpretationUsage(0);
        setInterpretationsUsed(0);
      }
    } catch (error) {
      console.warn('Failed to reset interpretation usage:', error);
    }
  }, [persistInterpretationUsage]);

  const formatWeiPrice = useCallback((wei: string | null) => {
    if (!wei) return null;
    const normalizedWei = wei.trim();
    if (!normalizedWei) return null;

    try {
      const weiValue = BigInt(normalizedWei);
      const formatted = formatEther(weiValue);
      const numericEth = Number.parseFloat(formatted);
      if (!Number.isFinite(numericEth) || numericEth <= 0) {
        return null;
      }
      return formatted;
    } catch (error) {
      console.warn('Failed to format wei price precisely:', error);

      const numeric = Number.parseFloat(normalizedWei);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        return null;
      }

      const ethValue = numeric / 1e18;
      if (!Number.isFinite(ethValue) || ethValue <= 0) {
        return null;
      }
      return ethValue.toString();
    }
  }, []);

  const effectiveInterpretationLimit = serverInterpretationLimit ?? interpretationDailyLimit;
  const remainingInterpretations = Math.max(effectiveInterpretationLimit - interpretationsUsed, 0);
  const effectiveInterpretationPriceUsd = serverInterpretationPrice ?? interpretationPriceUsd;
  const priceLabel = useMemo(() => {
    const resolvedWei = serverInterpretationPriceWei ?? interpretationPriceWei;
    const formattedWei = formatWeiPrice(resolvedWei);
    if (formattedWei) {
      return `$ETH ${formattedWei}`;
    }

    if (interpretationPriceEthString) {
      return `$ETH ${interpretationPriceEthString}`;
    }

    if (!Number.isNaN(interpretationPriceEth) && interpretationPriceEth > 0) {
      return `$ETH ${interpretationPriceEth.toString()}`;
    }

    return `$${effectiveInterpretationPriceUsd.toFixed(2)}`;
  }, [
    formatWeiPrice,
    interpretationPriceEth,
    interpretationPriceEthString,
    interpretationPriceWei,
    serverInterpretationPriceWei,
    effectiveInterpretationPriceUsd,
  ]);

  // Use the new Mini App authentication hook
  const {
    user,
    dbUser,
    isAuthenticated,
    isLoading: authLoading,
    reauthenticate,
  } = useMiniAppAuth();

  useEffect(() => {
    latestUserRef.current = user;
  }, [user]);

  useEffect(() => {
    latestAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;
    const detectHost = async () => {
      try {
        const isMiniApp = await sdk.isInMiniApp();
        if (!isMiniApp) return;

        const context = await sdk.context;
        const referrerDomain = context.location?.type === 'open_miniapp'
          ? context.location.referrerDomain
          : context.location?.referrerDomain ?? '';
        const inferredReferrer = referrerDomain || inferReferrerDomain(document.referrer);
        const userAgent = navigator.userAgent.toLowerCase();
        const baseFromUa = userAgent.includes('coinbase') || userAgent.includes('base');
        const baseFromReferrer = isBaseAppReferrer(inferredReferrer);

        if (!cancelled) {
          setMiniAppReferrer(inferredReferrer || null);
          setIsBaseApp(baseFromReferrer || baseFromUa);
        }
      } catch (error) {
        console.warn('Mini app host detection failed:', error);
      }
    };

    detectHost();
    return () => {
      cancelled = true;
    };
  }, []);
  
  // PROPER FIX: Memoize unstable objects to prevent infinite loops
  const stableUser = useMemo(() => user, [user?.fid, user?.username, user?.displayName]);
  const stableDbUser = useMemo(() => dbUser, [dbUser?.id, dbUser?.fid]);
  const stableFid = useMemo(() => user?.fid?.toString(), [user?.fid]);
  
  // Set current FID for debug logger
  useEffect(() => {
    log.setCurrentFid(user?.fid || null);
  }, [user?.fid]);

  // Notify host (Base/Farcaster) that app is ready
  useEffect(() => {
    const announceReady = async () => {
      const payload = { type: 'READY', source: 'stradamus-miniapp' as const };
      try {
        if (typeof sdk.ready === 'function') {
          await sdk.ready();
          console.log('✅ Mini App ready signal sent via sdk.ready()');
        } else {
          console.warn('Mini App SDK ready() not available, sending postMessage fallback');
        }
      } catch (error) {
        console.warn('Mini App ready signal failed:', error);
      } finally {
        try {
          window.parent?.postMessage(payload, '*');
          window.postMessage(payload, '*');
          console.log('✅ Mini App ready postMessage fallback sent');
        } catch (postMessageError) {
          console.warn('Mini App ready postMessage failed:', postMessageError);
        }
      }
    };
    announceReady();
  }, []);

  // TEMPORARY HARDCODE: Skip dynamic hooks to isolate infinite loop issue  
  const isAdmin = user?.fid === 457246; // Only real admin account
  const isMaintenanceMode = false; // Hardcode maintenance mode off
  const maxQuestionLength = 70; // Reduced for better UX - shorter questions are more focused
  
  // Skip these hooks temporarily:
  // const { permissions: adminPermissions, isAdmin } = useAdminPermissions(stableFid);
  // const { isMaintenanceMode } = useMaintenanceMode();
  // const { value: maxQuestionLength } = useSetting('max_question_length', 120, stableFid);

  const cardsLeft = maxCards - selectedCards.length;

  const farcasterAccess = useFarcasterOnly();
  
  // Component cleanup and stuck modal protection
  useEffect(() => {
    isMountedRef.current = true;
    // Clear any stuck modal on component mount to prevent purple border bug
    setSelectedCardModal(null);
    // Clear any stuck save error on component mount to prevent orange button bug
    setSaveError(null);

    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // 🔥 COMPLEX: Device synchronization on focus/visibility changes
  // WHY NEEDED: Farcaster users often switch between devices/tabs, need to sync readings
  // WHY COMPLEX: Must prevent infinity loops while ensuring data consistency
  // RATE LIMITING: 10s debounce to prevent API spam from rapid focus changes
  useEffect(() => {
    let syncTimeoutId: NodeJS.Timeout;
    let lastSyncTime = 0;
    let isCurrentlySyncing = false;
    const SYNC_DEBOUNCE_MS = 10000; // 10 seconds minimum between sync attempts (balanced)
    
    const handleFocus = async () => {
      const now = Date.now();
      const timeSinceLastSync = now - lastSyncTime;
      
      if (isCurrentlySyncing) {
        console.log('🔄 Focus sync BLOCKED - already syncing');
        return;
      }
      
      if (timeSinceLastSync < SYNC_DEBOUNCE_MS) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Focus sync DEBOUNCED - too soon since last sync:', Math.round(timeSinceLastSync/1000) + 's ago, need ' + Math.round(SYNC_DEBOUNCE_MS/1000) + 's (NUCLEAR RATE LIMIT)');
        }
        return;
      }
      
      if (isAuthenticated && user && !isLoading) {
        isCurrentlySyncing = true;
        lastSyncTime = now;
        console.log('🔄 App focused - comprehensive device sync starting (NUCLEAR 30s debounce)');
        try {
          let freshLimits;
          
          if (!isApiCallAllowed('checkReadingLimits')) {
            freshLimits = getCachedResult('checkReadingLimits');
            if (freshLimits) {
              console.log('📋 Using cached reading limits due to rate limit');
            } else {
              console.log('⚠️ No cached reading limits available, skipping sync');
              return;
            }
          } else {
            const { checkReadingLimits } = await import('./utils/supabaseUtils');
            freshLimits = await checkReadingLimits(user.fid);
            if (freshLimits) {
              setCachedResult('checkReadingLimits', freshLimits);
            }
          }
          
          if (freshLimits && freshLimits.stats) {
            const newTotal = (freshLimits.stats.dailyUsed || 0) + (freshLimits.stats.additionalUsed || 0);
            const currentTotal = totalReadingsToday;
            
            console.log('🔄 Device sync: Current=' + currentTotal + ', Fresh=' + newTotal);
            
            safeSetState(setTotalReadingsToday, newTotal);
            safeSetState(setCanDraw, freshLimits.canDoDaily || false);
            safeSetState(setCanDoAdditional, freshLimits.canDoAdditional || false);
            safeSetState(setDailyRemaining, freshLimits.stats?.dailyRemaining || 0);
            safeSetState(setAdditionalRemaining, freshLimits.stats?.additionalRemaining || 0);
            setPreValidatedLimits(freshLimits);
            
            if (newTotal >= 3 && currentTotal < 3) {
              console.log('🔄 Device sync detected readings completed on another device');
            }
            
            console.log('✅ Enhanced device sync completed on focus:', {
              totalReadings: newTotal,
              canDoDaily: freshLimits.canDoDaily,
              canDoAdditional: freshLimits.canDoAdditional
            });
          }

          try {
            let dbStreak;
            
            if (!isApiCallAllowed('getUserStreak')) {
              dbStreak = getCachedResult('getUserStreak');
              if (!dbStreak) {
                console.log('⚠️ No cached streak available, skipping streak sync');
                return;
              }
            } else {
              if (!user.fid || isNaN(user.fid) || user.fid <= 0) {
                console.error('❌ STREAK LOAD: Invalid FID:', user.fid);
                return;
              }
              
              const { getUserStreak } = await import('./utils/supabaseUtils');
              dbStreak = await getUserStreak(user.fid.toString());
              if (dbStreak) {
                setCachedResult('getUserStreak', dbStreak);
              }
            }
            
            if (dbStreak && dbStreak.count !== undefined) {
              const currentLocalStreak = getStreak();
              if (dbStreak.count !== currentLocalStreak) {
                safeSetState(setStreakCount, dbStreak.count);
                localStorage.setItem('tarot_streak', dbStreak.count.toString());
              }
            }
          } catch (streakError) {
            console.warn('⚠️ Device focus streak sync failed:', streakError);
          }
        } catch (error) {
          console.warn('⚠️ Enhanced device sync failed on focus:', error);
        } finally {
          isCurrentlySyncing = false;
        }
      } else {
        console.log('🔄 Focus sync skipped - not authenticated or still loading');
      }
    };

    const debouncedFocus = () => {
      clearTimeout(syncTimeoutId);
      syncTimeoutId = setTimeout(handleFocus, 1000);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        debouncedFocus(); // Also sync when tab becomes visible
      }
    };

    window.addEventListener('focus', debouncedFocus);
    window.addEventListener('visibilitychange', handleVisibilityChange);
    void handleFocus();
    
    return () => {
      clearTimeout(syncTimeoutId);
      window.removeEventListener('focus', debouncedFocus);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, user?.fid, isLoading]); // Removed totalReadingsToday to prevent infinity loop
  
  // Debug admin permissions (without exposing FID)
  useEffect(() => {
    if (user?.fid) {
      console.log('🔍 Admin check:', {
        hasUser: !!user,
        isAdmin,
        isAuthenticated
      });
    }
  }, [user?.fid, isAdmin, isAuthenticated]); // Removed adminPermissions object to prevent rerenders
  
  // Additional safety check for admin users
  if (isAuthenticated && user && isAdmin && isMaintenanceMode) {
    console.log('⚠️ Admin user bypassing maintenance mode');
  }

  // Call sdk.actions.ready() to ensure splash screen disappears in Farcaster mini app
  useEffect(() => {
    const callReady = async () => {
      try {
        // Log app version for debugging
        console.log('🔧 App Version: 2025-08-12-LIMIT-FIX');
        console.log('🔧 Check: NO legacy alert() functions for Database limits');
        
        // Override any potential legacy alert function
        const originalAlert = window.alert;
        window.alert = function(message) {
          if (typeof message === 'string' && message.includes('Database limits reached')) {
            console.log('🚫 BLOCKED legacy alert:', message);
            console.log('🔧 Using new limit system instead');
            return;
          }
          return originalAlert.call(window, message);
        };
        
        // Clear any browser cache
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then(function(registrations) {
            for(let registration of registrations) {
              registration.unregister();
            }
          });
        }
        
        // Check if we're in a mini app environment
        const isMiniApp = await sdk.isInMiniApp();
        
        if (isMiniApp) {
          console.log('🚀 Calling Farcaster SDK ready()...');
          await sdk.actions.ready();
          console.log('✅ Farcaster SDK ready() called successfully');
        } else {
          console.log('ℹ️ Not running in Farcaster mini app environment');
        }
      } catch (error) {
        console.warn('⚠️ Error calling Farcaster SDK ready():', error);
      }
    };

    callReady();
  }, []);

  // 1. Create or update user on app start when Farcaster user is available - ENHANCED
  useEffect(() => {
    const handleFarcasterUser = async () => {
      if (isAuthenticated && user) {
        try {
          console.log('🔄 DETAILED: Creating/updating user in Supabase:', {
            fid: user.fid,
            username: user.username,
            display_name: user.display_name,
            pfp_url: user.pfp_url
          });
          
          const createdUser = await createOrUpdateUser(user);
          console.log('✅ User successfully created/updated in Supabase:', createdUser);
          
          // Verify Supabase connection
          if (supabase) {
            try {
              const { data: session, error: sessionError } = await supabase.auth.getSession();
              if (sessionError) {
                console.warn('⚠️ Supabase session error:', sessionError);
              } else {
                console.log('🔐 Supabase session status:', session ? 'Active' : 'No session');
              }
            } catch (sessionError) {
              console.warn('⚠️ Could not check Supabase session:', sessionError);
            }
          }
        } catch (error) {
          console.error('❌ CRITICAL: Failed to handle Farcaster user:', error);
          console.error('❌ User data that failed:', user);
          console.error('❌ This will prevent app from working properly');
          
          // Continue execution but log the failure for debugging
          console.warn('⚠️ App will continue but some features may not work');
        }
      } else {
        console.log('⚠️ Skipping user creation:', { isAuthenticated, hasUser: !!user });
      }
    };

    handleFarcasterUser();
  }, [isAuthenticated, user?.fid]); // Use stable FID instead of full user object

  useEffect(() => {
    const initializeApp = async () => {
      // APPLY WORKING SOLUTION TO ALL ACCOUNTS: Minimal safe initialization
      if (true) { // Use minimal path for ALL accounts until we fix the complex initialization
        log.info('MINIMAL: Basic initialization for FID:', user?.fid || 'unknown');
        
        // Generate daily cards
        const dailyCards = generateDailyCards(activeDeck);
        setAvailableCards(dailyCards);
        
        // Add device sync for problem accounts (SAFE VERSION)
        if (isAuthenticated && user && user.fid) {
          log.sync('Starting reading fetch for FID:', user.fid);
          setReadingHistoryLoading(true);
          try {
            // Critical: Validate FID before API call
            if (!user.fid || isNaN(user.fid) || user.fid <= 0) {
              log.error('MINIMAL SYNC: Invalid FID:', user.fid);
              setIsLoading(false);
              return;
            }
            
            // Load existing reading if any
            const { getReadingHistoryByFid } = await import('./utils/supabaseUtils');
            const readings = await getReadingHistoryByFid(user.fid, 20);
            log.debug('Raw readings from DB:', readings);

            applyReadingHistory(readings);
            
            // Set default reading limits first, then override if we have data
            let dailyUsed = 0;
            let additionalUsed = 0;
            let todayReadings = [];
            
            if (readings && readings.length > 0) {
              // Filter today's readings
              const today = new Date().toISOString().split('T')[0];
              todayReadings = readings.filter(reading => {
                const readingDate = new Date(reading.created_at).toISOString().split('T')[0];
                return readingDate === today;
              });
              
              // Calculate usage based on today's readings
              dailyUsed = todayReadings.filter(r => r.reading_type === 'daily').length;
              additionalUsed = todayReadings.filter(r => r.reading_type === 'additional').length;
            }
            
            // ALWAYS set reading limits regardless of whether there are readings or not
            log.sync('Today readings count:', todayReadings.length);
            setTotalReadingsToday(todayReadings.length);
            
            // Calculate remaining readings
            safeSetState(setDailyRemaining, Math.max(0, 1 - dailyUsed));
            safeSetState(setAdditionalRemaining, Math.max(0, 2 - additionalUsed));
            
            // Set canDraw flags based on remaining readings 
            const canDoDaily = (1 - dailyUsed) > 0;
            const canDoAdditional = (2 - additionalUsed) > 0;
            safeSetState(setCanDraw, canDoDaily);
            safeSetState(setCanDoAdditional, canDoAdditional);
            
            log.debug('Reading limits set', {
              dailyUsed,
              additionalUsed,
              dailyRemaining: Math.max(0, 1 - dailyUsed),
              additionalRemaining: Math.max(0, 2 - additionalUsed),
              canDoDaily,
              canDoAdditional
            });
            
            // Load existing reading UI if we have readings from today
            if (todayReadings.length > 0) {
                // Sort by created_at DESC to get the most recent reading
                const sortedReadings = todayReadings.sort((a, b) => 
                  new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
                const latestReading = sortedReadings[0];
                
                log.minimal('Loading latest reading');
                const hydratedSelected = (latestReading.selected_cards || []).map((card: any) => hydrateCard(card));
                const selectedDeckKey = (latestReading.deck_key ?? hydratedSelected[0]?.deckKey ?? activeDeck) as DeckKey;

                if (selectedDeckKey !== activeDeck) {
                  setActiveDeck(selectedDeckKey);
                }

                setSelectedCards(hydratedSelected);

                const baseAvailable = generateDailyCards(selectedDeckKey);
                setAvailableCards(baseAvailable.filter(card => !hydratedSelected.some(selected => selected.id === card.id)));
                setQuestionText(latestReading.question_text || '');
                setReadingType(latestReading.reading_type || 'daily');
                setIsReadingComplete(true);
                setIsReadingSaved(true);
                setShowActionButtons(true);
                
                // Update streak for existing reading
                try {
                  log.streak('Updating streak...');
                  const { updateUserStreak } = await import('./utils/supabaseUtils');
                  const streakResult = await updateUserStreak(user.fid);
                  if (streakResult && streakResult.streak) {
                    log.minimal('Streak updated:', streakResult.streak.count);
                    
                    // Use streak count from database
                    setStreakCount(streakResult.streak.count);
                    console.log('🔥 MINIMAL SYNC: Streak count set to:', streakResult.streak.count);
                    
                    localStorage.setItem('tarot_streak', streakResult.streak.count.toString());
                  }
                } catch (streakError) {
                  log.error('MINIMAL SYNC: Streak update failed:', streakError);
                }
              }
            
          } catch (error) {
            console.error('❌ MINIMAL SYNC: Error loading reading:', error);
            // Even if sync fails, set default limits for fallback
            console.log('🔢 MINIMAL SYNC: Error fallback - setting default reading limits');
            safeSetState(setTotalReadingsToday, 0);
            safeSetState(setDailyRemaining, 1);
            safeSetState(setAdditionalRemaining, 2);
            safeSetState(setCanDraw, true);
            safeSetState(setCanDoAdditional, false);
          }
          finally {
            setReadingHistoryLoading(false);
          }
          
          // CRITICAL: Always load streak from database, even if no readings today
          try {
            log.streak('Loading streak from database...');
            const { getUserStreak } = await import('./utils/supabaseUtils');
            const dbStreak = await getUserStreak(user.fid);
            if (dbStreak && dbStreak.count !== undefined) {
              setStreakCount(dbStreak.count);
              localStorage.setItem('tarot_streak', dbStreak.count.toString());
              console.log('🔥 MINIMAL SYNC: Streak loaded from DB:', dbStreak.count);
            } else {
              console.log('🔥 MINIMAL SYNC: No streak in DB, keeping local:', streakCount);
            }
          } catch (streakError) {
            console.error('❌ MINIMAL SYNC: Failed to load streak:', streakError);
          }
        } else {
          // Unauthenticated users: Set default reading limits for localStorage-only mode
          console.log('🔢 MINIMAL: Setting default reading limits for unauthenticated user');
          safeSetState(setTotalReadingsToday, 0);
          safeSetState(setDailyRemaining, 1);
          safeSetState(setAdditionalRemaining, 2);
          safeSetState(setCanDraw, true);
          safeSetState(setCanDoAdditional, false);
        }
        
        setIsLoading(false);
        log.minimal('MINIMAL: Setup complete with sync');
        return;
      }
      
      // Don't initialize until auth is complete
      if (authLoading) {
        console.log('⏳ Waiting for auth to complete...');
        return;
      }
      
      console.log('🚀 App initialization started AFTER auth completed...', { 
        authLoading,
        isAuthenticated, 
        hasUser: !!stableUser, 
        hasDbUser: !!stableDbUser,
        isLoading
      });
      
      // Skip initialization if we're still loading auth data or already initialized
      if (isAuthenticated === undefined || (isAuthenticated && !stableUser) || !isLoading) {
        console.log('⏳ Authentication still loading or already initialized...');
        return;
      }
      
      // DISABLE error suppression - it causes infinite retry loops
      // initializeErrorSuppression();
      
      // Handle referral data from URL
      const referralData = extractReferralData();
      if (referralData.hasReferrer) {
        saveReferralData(referralData);
        console.log('Referral detected:', referralData.referrerFid);
      }

      // Development reset functionality
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('reset') === 'true' && import.meta.env.DEV) {
        localStorage.clear();
        console.log('Development reset: localStorage cleared');
        // Reload without reset parameter
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }

      // CRITICAL FIX: Handle both authenticated and unauthenticated users
      let authoritativeReadingCount = 0;
      let canDrawNow = true;
      let canDrawAdditional = false;

      if (isAuthenticated && user) {
        // Authenticated user - use database
        try {
          console.log('🔍 Getting fresh reading limits for authenticated user:', user.fid);
          const { checkReadingLimits } = await import('./utils/supabaseUtils');
          const freshLimits = await checkReadingLimits(user.fid);
          
          if (freshLimits && freshLimits.stats) {
            authoritativeReadingCount = freshLimits.stats.totalUsed || 0;
            canDrawNow = freshLimits.canDoDaily || false;
            canDrawAdditional = freshLimits.canDoAdditional || false;
            
            // Cache limits for performance optimization
            setPreValidatedLimits(freshLimits);
            
            // Update remaining counters
            safeSetState(setDailyRemaining, freshLimits.stats?.dailyRemaining || 0);
            safeSetState(setAdditionalRemaining, freshLimits.stats?.additionalRemaining || 0);
            
            console.log('✅ Fresh limits from database:', {
              totalUsed: authoritativeReadingCount,
              canDoDaily: canDrawNow,
              canDoAdditional: canDrawAdditional,
              dailyUsed: freshLimits.stats.dailyUsed,
              additionalUsed: freshLimits.stats.additionalUsed
            });
          } else {
            console.warn('⚠️ No database limits available for authenticated user');
            canDrawNow = canDrawToday();
          }
        } catch (error) {
          console.error('❌ Failed to get database limits for authenticated user:', error);
          canDrawNow = canDrawToday();
        }
      } else {
        // Unauthenticated user - use localStorage
        console.log('📋 Unauthenticated user - using localStorage limits');
        canDrawNow = canDrawToday();
        authoritativeReadingCount = canDrawNow ? 0 : 3; // Simple logic for unauth
      }

      // Set state ONCE with authoritative data
      setTotalReadingsToday(authoritativeReadingCount);
      setCanDraw(canDrawNow);
      setCanDoAdditional(canDrawAdditional);

      // Always generate today's cards for display
      const dailyCards = generateDailyCards(activeDeck);
      setAvailableCards(dailyCards);

      // Load LATEST reading for display across ALL devices (authenticated users only)  
      const authCheck = { isAuthenticated, hasUser: !!user, hasFid: !!user?.fid, hasDbUser: !!dbUser };
      console.log('🔍 CARD LOADING CHECK:', authCheck);
      setDebugInfo(`AUTH: ${JSON.stringify(authCheck)}`);
      
      if (isAuthenticated && user && user.fid) {
        console.log('🔄 DEVICE SYNC: Starting reading fetch for FID:', user.fid);
        setReadingHistoryLoading(true);
        try {
          // Critical: Validate FID before API call
          if (!user.fid || isNaN(user.fid) || user.fid <= 0) {
            console.error('❌ DEVICE SYNC: Invalid FID:', user.fid);
            return;
          }
          
          const { getReadingHistoryByFid } = await import('./utils/supabaseUtils');
          console.log('📋 DEVICE SYNC: About to call getReadingHistoryByFid...');
          const readings = await getReadingHistoryByFid(user.fid, 20);
          console.log('📋 DEVICE SYNC: Raw readings from DB:', readings);

          applyReadingHistory(readings);

          if (!readings || readings.length === 0) {
            console.log('📋 DEVICE SYNC: No readings found in DB for FID:', user.fid);
            return;
          }
          
          // Find the LATEST reading from TODAY across all devices
          const now = new Date();
          const todayUTC = now.toISOString().split('T')[0]; // YYYY-MM-DD format in UTC
          if (process.env.NODE_ENV === 'development') {
            console.log('📋 DEVICE SYNC: Current time:', now.toISOString());
            console.log('📋 DEVICE SYNC: Current timezone offset (minutes):', now.getTimezoneOffset());
            console.log('📋 DEVICE SYNC: Today UTC date:', todayUTC);
            console.log('📋 DEVICE SYNC: Processing', readings.length, 'readings from API...');
          }
          
          const todayReadings = readings.filter((reading: any) => {
            const createdAt = typeof reading?.created_at === 'string' ? reading.created_at : '';
            const readingUTCDate = createdAt.split('T')[0]; // Extract YYYY-MM-DD from UTC timestamp
            const isToday = readingUTCDate === todayUTC;
            console.log('📋 DEVICE SYNC: Reading', (reading?.id ?? '').toString().slice(0, 8), {
              created_at: createdAt,
              readingUTCDate,
              todayUTC,
              isToday,
            });
            return isToday;
          });
          
          console.log('📋 DEVICE SYNC: Found', todayReadings.length, 'readings for today');
          
          // 🐛 CRITICAL FIX: Update totalReadingsToday with actual filtered count from today  
          // STRANGE ISSUE: API returns 5 readings total, but only 2 are from today
          // WHY NEEDED: Frontend must show accurate count (2) not API total (5)
          // This prevents showing wrong counts when API returns readings from multiple days
          safeSetState(setTotalReadingsToday, todayReadings.length);
          console.log('📋 DEVICE SYNC: Updated totalReadingsToday to:', todayReadings.length);
          
          if (todayReadings.length === 0) {
            console.log('📋 DEVICE SYNC: No readings found for today - starting fresh');
            setDebugInfo(prev => prev + ' | DB: No readings today');
            return;
          }
          
          // Sort by created_at DESC to get the most recent reading
          const sortedReadings = todayReadings.sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          
          const latestReading = sortedReadings[0];
          console.log('📋 DEVICE SYNC: Latest reading:', {
            id: latestReading.id,
            created_at: latestReading.created_at,
            selected_cards: latestReading.selected_cards,
            selected_cards_length: latestReading.selected_cards?.length,
            question_text: latestReading.question_text
          });
          
          if (!latestReading.selected_cards || latestReading.selected_cards.length === 0) {
            console.log('📋 DEVICE SYNC: Latest reading has no selected_cards field');
            return;
          }
          
          const hydratedSelected = latestReading.selected_cards.map((card: any) => hydrateCard(card));

          if (hydratedSelected.length !== 3) {
            console.log('📋 DEVICE SYNC: Latest reading incomplete, cards:', hydratedSelected.length);
            return;
          }
          const selectedDeckKey = (latestReading.deck_key ?? hydratedSelected[0]?.deckKey ?? activeDeck) as DeckKey;
          const questionTextFromReading = latestReading.question_text || '';

          console.log('📋 DEVICE SYNC: About to load cards into UI:', {
            cardsToLoad: hydratedSelected.map(c => ({ id: c.id, name: c.name })),
            questionText: questionTextFromReading,
            deckKey: selectedDeckKey,
          });
          
          // CRITICAL: Show reading but DON'T modify totalReadingsToday
          safeSetState(setSelectedCards, hydratedSelected);
          safeSetState(setQuestionText, questionTextFromReading);

          if (selectedDeckKey !== activeDeck) {
            setActiveDeck(selectedDeckKey);
          }

          // Remove selected cards from available cards
          const baseAvailable = generateDailyCards(selectedDeckKey);
          const filteredAvailable = baseAvailable.filter(card => !hydratedSelected.some(selected => selected.id === card.id));
          safeSetState(setAvailableCards, filteredAvailable);
          
          safeSetState(setQuestionText, questionTextFromReading);
          safeSetState(setIsReadingComplete, true);
          safeSetState(setIsReadingSaved, true);
          safeSetState(setShowActionButtons, true);
          
          console.log('✅ DEVICE SYNC: Successfully loaded reading from DB');
          setDebugInfo(prev => prev + ' | DB: Cards loaded!!');
          
          // For admin users, log additional debug info
          if (isAdmin) {
            console.log('🔧 ADMIN DEBUG: Card loading completed', {
              selectedCardsCount: hydratedSelected.length,
              questionText: questionTextFromReading,
              readingComplete: true,
              isAdmin: true
            });
          }
          
          // CRITICAL: Update streak when loading existing reading on second device
          try {
            console.log('🔥 DEVICE SYNC: Updating streak for existing reading...');
            const { updateUserStreak } = await import('./utils/supabaseUtils');
            const streakResult = await updateUserStreak(user.fid);
            if (streakResult && streakResult.streak) {
              console.log('✅ DEVICE SYNC: Streak updated for existing reading:', streakResult.streak.count);
              
              // Use streak count from database
              safeSetState(setStreakCount, streakResult.streak.count);
              console.log('🔥 DEVICE SYNC: Streak count set to:', streakResult.streak.count);
              
              localStorage.setItem('tarot_streak', streakResult.streak.count.toString());
            } else {
              console.log('⚠️ DEVICE SYNC: Streak update returned null - may need UNIQUE constraint fix');
            }
          } catch (streakError) {
            console.error('❌ DEVICE SYNC STREAK ERROR:', streakError);
            console.warn('⚠️ DEVICE SYNC: Failed to update streak for existing reading');
          }
          
        } catch (error) {
          console.error('❌ DEVICE SYNC: Critical error loading reading:', error);
          console.error('❌ DEVICE SYNC: Error stack:', error.stack);
        } finally {
          setReadingHistoryLoading(false);
        }
      } else {
        // User not authenticated OR missing data - try localStorage fallback
        console.log('📋 DB card loading skipped - trying localStorage fallback...', { 
          isAuthenticated, 
          hasUser: !!user, 
          hasFid: !!user?.fid 
        });
      setReadingHistory([]);
        setReadingHistoryLoading(false);
        const previousReading = getDailyReading();
        if (previousReading && previousReading.selectedCards.length === 3) {
          console.log('📋 Loaded reading from localStorage');
          setDebugInfo(prev => prev + ' | LS: Cards loaded!');

          const hydratedLocal = previousReading.selectedCards.map(card => hydrateCard(card));
          const localDeckKey = hydratedLocal[0]?.deckKey ?? activeDeck;

          if (localDeckKey !== activeDeck) {
            setActiveDeck(localDeckKey);
          }

          safeSetState(setSelectedCards, hydratedLocal);
          const baseAvailable = generateDailyCards(localDeckKey);
          const filteredAvailable = baseAvailable.filter(card => !hydratedLocal.some(selected => selected.id === card.id));
          safeSetState(setAvailableCards, filteredAvailable);
          safeSetState(setIsReadingComplete, true);
          safeSetState(setIsReadingSaved, true);
          safeSetState(setShowActionButtons, true);
        }
      }

      // Preload images in background for better UX
      const imageUrls = dailyCards.map(card => card.image);
      const uniqueImageUrls = [...new Set(imageUrls)]; // Remove duplicates since all cards use same image
      
      try {
        await preloadImages(uniqueImageUrls, (loaded, total) => {
          setPreloadProgress((loaded / total) * 100);
        });
        console.log('Images preloaded successfully');
      } catch (error) {
        console.warn('Image preloading failed:', error);
      }

      safeSetState(setIsLoading, false);
      
      // Sync streak with database (authenticated users only) - ENHANCED for cross-device sync
      if (isAuthenticated && stableUser && stableDbUser) {
        try {
          // CRITICAL FIX: Validate FID before using to prevent ReferenceError
          if (!stableUser.fid || isNaN(stableUser.fid) || stableUser.fid <= 0) {
            console.error('❌ STREAK SYNC: Invalid FID:', stableUser.fid);
            return;
          }
          
          console.log('🔥 Syncing streak from database for FID:', user.fid);
          const { getUserStreak } = await import('./utils/supabaseUtils');
          const dbStreak = await getUserStreak(user.fid.toString());
          
          if (dbStreak && dbStreak.count !== undefined) {
            console.log('🔥 Database streak found:', dbStreak.count, '(current frontend:', streakCount, ')');
            
            // ALWAYS update frontend with database value for cross-device consistency
            const currentLocalStreak = getStreak();
            if (dbStreak.count !== currentLocalStreak) {
              console.log('🔄 Cross-device streak sync: DB(' + dbStreak.count + ') != Local(' + currentLocalStreak + ')');
            }
            
            // Use streak count from database
            safeSetState(setStreakCount, dbStreak.count);
            // Keep localStorage in sync across devices
            localStorage.setItem('tarot_streak', dbStreak.count.toString());
            console.log('✅ Streak synced from DB for device consistency:', dbStreak.count);
          } else {
            // No streak in database yet - this is expected for new users
            console.log('🔥 No streak in database yet - keeping current:', streakCount);
            // But still try to ensure consistency by checking if we have any readings
            try {
              const { getReadingHistoryByFid } = await import('./utils/supabaseUtils');
              const allReadings = await getReadingHistoryByFid(user.fid, 10);
              
              if (allReadings && allReadings.length > 0) {
                // User has readings but no streak record - this might indicate data inconsistency
                console.log('⚠️ User has readings but no streak record - this may need attention');
              }
            } catch (streakCheckError) {
              console.warn('⚠️ Could not verify reading history for streak consistency:', streakCheckError);
            }
          }
        } catch (error) {
          console.warn('⚠️ Failed to sync streak from DB:', error);
          // Keep local streak if DB sync fails, but log for debugging
          console.warn('📱 Device sync: keeping local streak due to DB error');
        }
      } else {
        console.log('🔥 Using localStorage streak for unauthenticated user:', streakCount);
      }
      
      if (USE_LEGACY_ONBOARDING) {
        console.log('🔍 Onboarding check:', { isAuthenticated, user: !!user, dbUser: !!dbUser });

        const forceOnboarding = new URLSearchParams(window.location.search).get('onboarding') === 'true';
        if (forceOnboarding) {
          safeSetState(setShowOnboarding, true);
          console.log('🔧 Forcing onboarding due to URL parameter');
          return;
        }

        const hasSeenOnboarding = localStorage.getItem('onboarding_seen');
        console.log('📱 MVP onboarding check - localStorage seen:', hasSeenOnboarding);
        if (!hasSeenOnboarding) {
          safeSetState(setShowOnboarding, true);
          console.log('🎯 Showing onboarding (MVP mode - localStorage only)');
        } else {
          console.log('✅ Onboarding skipped (MVP mode - already seen in localStorage)');
        }
      } else if (typeof window !== 'undefined') {
        window.localStorage.setItem('onboarding_seen', 'true');
      }
    };

    initializeApp();
  }, [authLoading, isAuthenticated]); // Minimal dependencies for stability

  useEffect(() => {
    void promptAddMiniApp();
  }, [promptAddMiniApp]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ACTIVE_DECK_STORAGE_KEY, activeDeck);
    }
  }, [activeDeck]);

  const interpretationFeatureEnabled = useMemo(() => {
    if (!interpretationFlagEnabled) return false;
    if (!user?.fid) return false;
    const fid = Number(user.fid);
    if (Number.isNaN(fid) || fid <= 0) return false;
    if (!interpretationAllowedFids.length) return true;
    return interpretationAllowedFids.includes(fid);
  }, [interpretationFlagEnabled, interpretationAllowedFids, user?.fid]);

  const syncInterpretationUsage = useCallback(async () => {
    if (!interpretationFeatureEnabled) return;
    const fid = user?.fid ? Number(user.fid) : NaN;
    if (Number.isNaN(fid) || fid <= 0) return;

    try {
      const usage = await fetchInterpretationUsage(fid);
      setInterpretationsUsed(usage.usedToday);
      setServerInterpretationLimit(
        typeof usage.dailyLimit === 'number' ? usage.dailyLimit : null,
      );
      setServerInterpretationPrice(
        typeof usage.priceUsd === 'number' ? usage.priceUsd : null,
      );
      setServerInterpretationPriceWei(
        typeof usage.priceWei === 'string' ? usage.priceWei : null,
      );
      persistInterpretationUsage(usage.usedToday);
    } catch (error) {
      console.warn('Failed to sync interpretation usage:', error);
    }
  }, [interpretationFeatureEnabled, user?.fid, persistInterpretationUsage]);

  useEffect(() => {
    if (!interpretationFeatureEnabled) {
      setInterpretationState('idle');
      setInterpretationText('');
      setInterpretationError(null);
      setInterpretationsUsed(0);
      setServerInterpretationLimit(null);
      setServerInterpretationPrice(null);
      return;
    }
    loadInterpretationUsage();
    resetInterpretationUsageIfNeeded();
    syncInterpretationUsage();
  }, [interpretationFeatureEnabled, loadInterpretationUsage, resetInterpretationUsageIfNeeded, syncInterpretationUsage]);

  useEffect(() => {
    if (!interpretationFeatureEnabled) return;
    if (interpretationState === 'error' && interpretationError) {
      alert(interpretationError);
      setInterpretationState('idle');
      setInterpretationError(null);
    }
  }, [interpretationFeatureEnabled, interpretationState, interpretationError]);

  const handleOpenInterpretationModal = useCallback(() => {
    if (!interpretationFeatureEnabled) return;
    if (!isReadingComplete || selectedCards.length < maxCards) return;

    resetInterpretationUsageIfNeeded();
    void syncInterpretationUsage();

    if (interpretationText) {
      setInterpretationError(null);
      setInterpretationState('ready');
      return;
    }

    if (!user?.fid && !mockInterpretationEnabled) {
      alert(`Sign in with Farcaster to request a ${interpretationLabel}.`);
      return;
    }

    if (remainingInterpretations <= 0) {
      alert('You have reached the daily limit of insights. Come back tomorrow!');
      return;
    }

    setInterpretationError(null);
    setInterpretationState('modal');
  }, [
    interpretationFeatureEnabled,
    isReadingComplete,
    selectedCards,
    maxCards,
    interpretationText,
    user?.fid,
    remainingInterpretations,
    resetInterpretationUsageIfNeeded,
    syncInterpretationUsage,
    interpretationLabel,
    mockInterpretationEnabled,
  ]);

  const handleCloseInterpretationModal = useCallback(() => {
    if (interpretationState === 'processing') return;
    setInterpretationState('idle');
  }, [interpretationState]);

  const handleConfirmInterpretation = useCallback(async () => {
    if (!interpretationFeatureEnabled || interpretationState === 'processing') return;
    if (!user?.fid && !mockInterpretationEnabled) {
      setInterpretationError(`Sign in required to request a ${interpretationLabel}.`);
      setInterpretationState('error');
      return;
    }
    if (!selectedCards.length) return;

    const fallbackFid = Number.isFinite(Number(user?.fid)) && Number(user?.fid) > 0
      ? Number(user?.fid)
      : 999999;
    const fid = mockInterpretationEnabled ? fallbackFid : Number(user?.fid);
    if (!mockInterpretationEnabled && (Number.isNaN(fid) || fid <= 0)) {
      setInterpretationError('Invalid Farcaster identity for interpretations.');
      setInterpretationState('error');
      return;
    }

    setInterpretationState('processing');
    setInterpretationError(null);

    try {
      if (forceInterpretationPaymentError) {
        throw new PaymentError('Forced payment error (VITE_FORCE_INTERPRETATION_PAYMENT_ERROR)');
      }

      if (!mockInterpretationEnabled) {
        const paymentDetails = await createInterpretationPayment({
          fid,
          deckKey: activeDeck,
        });

        if (typeof paymentDetails?.priceWei === 'string') {
          setServerInterpretationPriceWei(paymentDetails.priceWei);
        }

        const priceWeiString = paymentDetails?.priceWei;
        const receiver = paymentDetails?.receiverAddress;
        const chainIdCaip = paymentDetails?.chainId ?? `eip155:${paymentDetails?.chainReference ?? '8453'}`;

        if (!paymentDetails?.alreadyUnlocked) {
          const priceWeiBigInt = priceWeiString ? BigInt(priceWeiString) : 0n;

          if (priceWeiBigInt > 0n) {
            if (isBaseApp) {
              setInterpretationError('Payments are not supported in Base app yet. Open in Warpcast to continue.');
              setInterpretationState('error');
              return;
            }
            if (!receiver) {
              throw new PaymentError('Payment receiver address is not configured.');
            }

            if (typeof sdk?.wallet?.getEthereumProvider !== 'function') {
              throw new PaymentError('Wallet access is not available. Open this mini-app in Warpcast to continue.');
            }

            const provider = await sdk.wallet.getEthereumProvider();

            if (!provider) {
              throw new PaymentError('Wallet provider unavailable. Please retry inside Warpcast.');
            }

            const [, chainReferenceRaw] = chainIdCaip.split(':');
            const chainReferenceNumber = Number.parseInt(chainReferenceRaw ?? '8453', 10);
            const hexChainId = `0x${chainReferenceNumber.toString(16)}`;

            try {
              await provider.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: hexChainId }],
              });
            } catch (switchError) {
              const errorCode =
                typeof switchError === 'object' && switchError && 'code' in switchError
                  ? // @ts-expect-error runtime check
                    switchError.code
                  : null;

              if (errorCode === 4001) {
                throw new PaymentCancelledError('User rejected chain switch');
              }

              // Some wallets throw if already on the chain; ignore in that case
              if (errorCode !== 4902) {
                console.warn('wallet_switchEthereumChain failed, proceeding without explicit switch', switchError);
              }
            }

            let accounts: unknown;
            try {
              accounts = await provider.request({ method: 'eth_requestAccounts' });
            } catch (accountError) {
              const errorCode =
                typeof accountError === 'object' && accountError && 'code' in accountError
                  ? // @ts-expect-error runtime check
                    accountError.code
                  : null;

              if (errorCode === 4001) {
                throw new PaymentCancelledError('User rejected wallet connection');
              }

              throw new PaymentError('Unable to access wallet account.');
            }

            const fromAddress = Array.isArray(accounts) && accounts.length > 0 ? String(accounts[0]) : null;
            if (!fromAddress) {
              throw new PaymentError('Wallet account not found. Please reconnect your wallet.');
            }

            const valueHex = `0x${priceWeiBigInt.toString(16)}`;

            let txHash: unknown;
            try {
              txHash = await provider.request({
                method: 'eth_sendTransaction',
                params: [
                  {
                    from: fromAddress,
                    to: receiver,
                    value: valueHex,
                    data: '0x',
                  },
                ],
              });
            } catch (txError) {
              const errorCode =
                typeof txError === 'object' && txError && 'code' in txError
                  ? // @ts-expect-error runtime check
                    txError.code
                  : null;

              if (errorCode === 4001) {
                throw new PaymentCancelledError('Payment cancelled');
              }

              const message = txError instanceof Error ? txError.message : 'Failed to send transaction';
              throw new PaymentError(message);
            }

            if (typeof txHash !== 'string') {
              throw new PaymentError('Wallet did not return a transaction hash.');
            }

            try {
              await confirmInterpretationPayment({ fid, deckKey: activeDeck, txHash });
            } catch (confirmError) {
              if (confirmError instanceof PaymentError) {
                throw confirmError;
              }
              if (confirmError instanceof Error && confirmError.message?.toLowerCase().includes('timeout')) {
                throw new PaymentTimeoutError(confirmError.message);
              }
              throw confirmError instanceof Error
                ? confirmError
                : new PaymentError('Failed to confirm payment');
            }
          }
        }
      }

      const response = await requestInterpretation({
        fid,
        cards: selectedCards,
        deckKey: activeDeck,
        question: questionText,
      });

      setInterpretationText(response.interpretation);
      setInterpretationsUsed(response.usage.usedToday);
      setServerInterpretationLimit(
        typeof response.usage.dailyLimit === 'number' ? response.usage.dailyLimit : null,
      );
      setServerInterpretationPrice(
        typeof response.usage.priceUsd === 'number' ? response.usage.priceUsd : null,
      );
      setServerInterpretationPriceWei(
        typeof response.usage.priceWei === 'string' ? response.usage.priceWei : null,
      );
      persistInterpretationUsage(response.usage.usedToday);
      setInterpretationState('ready');
    } catch (error) {
      console.error('Interpretation flow failed:', error);
      let message = 'Failed to generate interpretation. Please try again.';
      let usage = undefined;

      if (error instanceof PaymentCancelledError) {
        message = 'Payment cancelled. We did not charge you.';
      } else if (error instanceof PaymentTimeoutError) {
        message = 'Payment is still pending. Once it confirms on-chain, reopen Magic Insight to reveal it without paying again.';
      } else if (error instanceof PaymentError) {
        const baseMessage = error.message || message;
        message = `${baseMessage} If funds were sent, reopen Magic Insight in a moment to claim the insight without another payment.`;
      } else if (error instanceof InterpretationError) {
        message = error.message || message;
        usage = error.usage;
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }

      log.error('Interpretation flow failed', error);

      if (usage) {
        setInterpretationsUsed(usage.usedToday);
        setServerInterpretationLimit(
          typeof usage.dailyLimit === 'number' ? usage.dailyLimit : null,
        );
        setServerInterpretationPrice(
          typeof usage.priceUsd === 'number' ? usage.priceUsd : null,
        );
        setServerInterpretationPriceWei(
          typeof usage.priceWei === 'string' ? usage.priceWei : null,
        );
        persistInterpretationUsage(usage.usedToday);
      }

      setInterpretationError(message);
      setInterpretationState('error');
    }
  }, [
    interpretationFeatureEnabled,
    interpretationState,
    user?.fid,
    selectedCards,
    activeDeck,
    questionText,
    persistInterpretationUsage,
    interpretationLabel,
    mockInterpretationEnabled,
    isBaseApp,
  ]);

  const openShareModal = useCallback(
    (mode: 'reading' | 'insight', insight?: string, shareUrl?: string | null) => {
      setShareContext(mode);
      setShareInsightText(insight ?? '');
      setShareUrlOverride(shareUrl ?? undefined);
      setShareModalOpen(true);
    },
    [],
  );

  const loadReadingFromHistory = useCallback(
    (reading: ReadingHistoryEntry) => {
      if (!reading || !Array.isArray(reading.cards) || reading.cards.length === 0) {
        console.warn('Reading history entry missing cards');
        return;
      }

      const hydratedCards = reading.cards.map((card) => hydrateCard(card));
      const deckKey = (reading.deckKey ?? hydratedCards[0]?.deckKey ?? activeDeck) as DeckKey;

      setActiveDeck((prev) => (prev === deckKey ? prev : deckKey));
      setSelectedCards(hydratedCards);

      const baseAvailable = generateDailyCards(deckKey);
      setAvailableCards(
        baseAvailable.filter((card) => !hydratedCards.some((selected) => selected.id === card.id)),
      );

      setQuestionText(reading.question ?? '');
      setReadingType(reading.readingType ?? 'daily');
      setIsReadingComplete(true);
      setIsReadingSaved(true);
      setShowActionButtons(true);
      setIsSaving(false);
      setSaveError(null);
      setShowFanHint(false);
      setSelectedCardModal(null);

      setInterpretationText(reading.insight ?? '');
      setInterpretationState(reading.insight ? 'ready' : 'idle');
      setShareUrlOverride(reading.shareUrl ?? undefined);
    },
    [activeDeck, generateDailyCards, hydrateCard],
  );

  const handleHistoryShare = useCallback(
    (reading: ReadingHistoryEntry) => {
      loadReadingFromHistory(reading);
      openShareModal('reading', undefined, reading.shareUrl ?? undefined);
    },
    [loadReadingFromHistory, openShareModal],
  );

  const handleHistoryRequestInsight = useCallback(
    (reading: ReadingHistoryEntry) => {
      if (!interpretationFeatureEnabled) {
        return;
      }

      loadReadingFromHistory(reading);

      resetInterpretationUsageIfNeeded();
      void syncInterpretationUsage();
      setInterpretationError(null);

      if (reading.insight) {
        setInterpretationText(reading.insight);
        setInterpretationState('ready');
      } else {
        setInterpretationText('');
        setInterpretationState('modal');
      }
    },
    [interpretationFeatureEnabled, loadReadingFromHistory, resetInterpretationUsageIfNeeded, syncInterpretationUsage],
  );

  const handleCloseInterpretationResult = useCallback(() => {
    setInterpretationState('idle');
    setInterpretationError(null);
    void syncInterpretationUsage();
  }, [syncInterpretationUsage]);

  const handleCopyInterpretation = useCallback(async () => {
    if (!interpretationText) {
      return false;
    }

    try {
      await navigator.clipboard.writeText(interpretationText);
      return true;
    } catch (error) {
      console.error('Failed to copy interpretation:', error);
      return false;
    }
  }, [interpretationText]);

  const handleShareInterpretation = useCallback(() => {
    if (!interpretationText) return;
    setShareUrlOverride(undefined);
    openShareModal('insight', interpretationText);
  }, [interpretationText, openShareModal]);

  const handleRequestAnotherInterpretation = useCallback(() => {
    if (interpretationState === 'processing') {
      return;
    }

    resetInterpretationUsageIfNeeded();
    void syncInterpretationUsage();

    if (remainingInterpretations <= 0) {
      alert('You have reached the daily limit of insights. Come back tomorrow!');
      return;
    }

    setInterpretationError(null);
    setInterpretationState('modal');
  }, [
    interpretationState,
    resetInterpretationUsageIfNeeded,
    syncInterpretationUsage,
    remainingInterpretations,
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ACTIVE_DECK_STORAGE_KEY, activeDeck);
    }
  }, [activeDeck]);

  const handleCardSelect = async (card: TarotCardData) => {
    handleFanInteract();
    // ENHANCED: Always validate limits for first card to prevent race conditions
    if (!user && !allowBrowserAccess) {
      console.error('❌ No user available for limit check');
      return;
    }

    // If this is the first card selection, always do fresh validation
    if (selectedCards.length === 0) {
      console.log('🔍 First card selection - checking cached limits first');
      
      let currentLimits = allowBrowserAccess ? { ...DEFAULT_READING_LIMITS } : null;
      
      // TRY CACHE FIRST to reduce API calls
      if (!currentLimits) {
        currentLimits = getCachedResult('checkReadingLimits');
      }
      
      if (!currentLimits && isApiCallAllowed('checkReadingLimits')) {
        console.log('🔄 Cache miss - making fresh API call');
        try {
          const { checkReadingLimits } = await import('./utils/supabaseUtils');
          currentLimits = await checkReadingLimits(user!.fid);
          if (currentLimits) {
            setCachedResult('checkReadingLimits', currentLimits);
          }
        } catch (error) {
          console.error('❌ Failed to fetch reading limits:', error);
        }
      } else if (!currentLimits) {
        console.log('⚠️ Using preValidatedLimits as fallback');
        currentLimits = preValidatedLimits;
      }
      
      if (currentLimits) {
        setPreValidatedLimits(currentLimits);
          
          // Update states with absolutely fresh data
          const freshTotal = (currentLimits.stats?.dailyUsed || 0) + (currentLimits.stats?.additionalUsed || 0);
          safeSetState(setTotalReadingsToday, freshTotal);
          safeSetState(setCanDraw, currentLimits.canDoDaily || false);
          safeSetState(setCanDoAdditional, currentLimits.canDoAdditional || false);
          safeSetState(setDailyRemaining, currentLimits.stats?.dailyRemaining || 0);
          safeSetState(setAdditionalRemaining, currentLimits.stats?.additionalRemaining || 0);
          
          console.log('✅ States synchronized with fresh server data:', {
            serverTotal: freshTotal,
            localTotal: totalReadingsToday,
            canDoDaily: currentLimits.canDoDaily,
            canDoAdditional: currentLimits.canDoAdditional
          });
        }
      
      if (!currentLimits) {
        console.error('❌ No current limits available from server');
        alert('Unable to check reading limits. Please refresh and try again.');
        return;
      }
      
      const totalUsed = (currentLimits.stats?.dailyUsed || 0) + (currentLimits.stats?.additionalUsed || 0);
      const maxTotal = 3; // daily (1) + additional (2)
      
      if (totalUsed >= maxTotal) {
        console.log('⚠️ Server validation: All readings used for today:', { 
          totalUsed, 
          maxTotal, 
          dailyUsed: currentLimits.stats?.dailyUsed,
          additionalUsed: currentLimits.stats?.additionalUsed,
          canDoDaily: currentLimits.canDoDaily,
          canDoAdditional: currentLimits.canDoAdditional
        });
        alert(`All readings completed (${totalUsed}/${maxTotal}). Come back tomorrow for more!`);
        return;
      }
      
      // Check if user can do the current reading type
      const canDoDaily = currentLimits.canDoDaily || false;
      const canDoAdditional = currentLimits.canDoAdditional || false;
      
      if (!canDoDaily && !canDoAdditional) {
        console.log('⚠️ Server validation: Neither daily nor additional readings available:', {
          dailyUsed: currentLimits.stats?.dailyUsed,
          additionalUsed: currentLimits.stats?.additionalUsed,
          dailyLimit: currentLimits.stats?.dailyLimit,
          additionalLimit: currentLimits.stats?.maxAdditional,
          canDoDaily,
          canDoAdditional
        });
        alert(`No readings available. Daily: ${currentLimits.stats?.dailyUsed || 0}/${currentLimits.stats?.dailyLimit || 1}, Additional: ${currentLimits.stats?.additionalUsed || 0}/${currentLimits.stats?.maxAdditional || 2}`);
        return;
      }
      
      // Update reading type based on fresh server data
      if (currentLimits.stats?.dailyUsed === 0) {
        setReadingType('daily');
        console.log('📋 Server validation: Setting reading type to daily');
      } else {
        setReadingType('additional');
        console.log('📋 Server validation: Setting reading type to additional');
      }
      
      console.log('✅ Server validation passed - proceeding with reading');
    }
    
    // Original card selection logic - now protected by limit checks
    if (selectedCards.length < maxCards && !selectedCards.find(c => c.id === card.id)) {
      const newSelectedCards = [...selectedCards, card];
      setSelectedCards(newSelectedCards);
      // Remove the selected card from available cards
      setAvailableCards(prev => prev.filter(c => c.id !== card.id));
      setSelectedCardModal(card);

      // If this completes the reading, prepare for save (but don't save yet)
      if (newSelectedCards.length === maxCards) {
        console.log('🎯 Reading completed! Cards selected:', newSelectedCards.map(c => c.id));
        
        // Mark reading as complete (but not yet saved)
        setIsReadingComplete(true);
        
        // Update meta tags for sharing with the selected cards
        const metaData = generateCardMetaTags(newSelectedCards);
        updateMetaTags(metaData);
        
        // Process referral if this is user's first reading
        const urlParams = new URLSearchParams(window.location.search);
        const mockUserFid = urlParams.get('mockFid') || 'unknown';
        processReferral(mockUserFid);

        // Save after animations complete (1 second delay - optimized)
        setTimeout(() => {
          // Check if component is still mounted unless we run in mock/offline mode
          if (!isMountedRef.current && !supabaseDisabled) {
            console.log('⚠️ Component unmounted, skipping save');
            return;
          }

          try {
            saveCompletedReading(newSelectedCards, questionText);
          } catch (error) {
            console.error('❌ Error saving reading:', error);
          }
        }, 1000);
      }
    }
  };

  const handleSelectedCardClick = (card: TarotCardData) => {
    setSelectedCardModal(card);
  };

  const handleShare = useCallback(() => {
    setShareUrlOverride(undefined);
    openShareModal('reading');
  }, [openShareModal]);

  // Function to start a new reading (reset state and generate new cards)
  const startNewReading = async () => {
    console.log('🔄 Starting new reading...');
    handleFanInteract();

    // ALWAYS reset states first - regardless of API calls
    console.log('🔄 Resetting all reading states...');
    setIsReadingComplete(false);
    setIsReadingSaved(false);
    setShowActionButtons(false);
    setIsSaving(false);
    setSelectedCards([]); // Critical: clear selected cards immediately
    setSelectedCardModal(null); // Clear any highlighted card
    setQuestionText('');
    setInterpretationState('idle');
    setInterpretationText('');
    setInterpretationError(null);
    
    // Generate fresh set of 8 cards immediately
    const newCards = generateDailyCards(activeDeck);
    setAvailableCards(newCards);
    console.log('✅ Generated fresh cards:', newCards.length);
    
    // Validate limits after state reset
    if (!user && !allowBrowserAccess) {
      console.error('❌ No user available for limit check');
      // Still allow reading in case of missing user (fallback)
      setCanDraw(true);
      setReadingType('daily');
      return;
    }
    
    let currentLimits = allowBrowserAccess ? { ...DEFAULT_READING_LIMITS } : null;
    
    // TRY CACHE FIRST to reduce API calls on new reading
    if (!currentLimits) {
      currentLimits = getCachedResult('checkReadingLimits');
    }
    
    if (!currentLimits && isApiCallAllowed('checkReadingLimits')) {
      console.log('🔄 Cache miss for new reading - making API call');
      try {
        const { checkReadingLimits } = await import('./utils/supabaseUtils');
        currentLimits = await checkReadingLimits(user!.fid);
        if (currentLimits) {
          setCachedResult('checkReadingLimits', currentLimits);
        }
      } catch (error) {
        console.error('❌ Failed to fetch reading limits:', error);
      }
    }
    
    if (!currentLimits) {
      console.error('❌ Failed to fetch current limits - continuing with default settings');
      setCanDraw(true);
      setReadingType('daily');
      return;
    }
    
    // Update cached limits for performance
    setPreValidatedLimits(currentLimits);
    
    console.log('🔍 Current limits check:', currentLimits);
    
    // Check if user can start a new reading
    const totalUsed = (currentLimits.stats?.dailyUsed || 0) + (currentLimits.stats?.additionalUsed || 0);
    const maxTotal = 3; // daily (1) + additional (2)
    
    if (totalUsed >= maxTotal) {
      console.log('⚠️ All readings used for today:', { 
        totalUsed, 
        maxTotal,
        dailyUsed: currentLimits.stats?.dailyUsed,
        additionalUsed: currentLimits.stats?.additionalUsed,
        canDoDaily: currentLimits.canDoDaily,
        canDoAdditional: currentLimits.canDoAdditional
      });
      alert(`All readings completed (${totalUsed}/${maxTotal}). You can still share your previous readings!`);
      return;
    }
    
    // Update states with fresh data
    const canDoDaily = currentLimits.canDoDaily || false;
    const canDoAdditional = currentLimits.canDoAdditional || false;
    
    setCanDraw(canDoDaily || canDoAdditional); // Can draw if either type is available
    setCanDoAdditional(canDoAdditional);
    setTotalReadingsToday(totalUsed);
    
    // Determine reading type based on fresh database data
    if (currentLimits.stats?.dailyUsed === 0) {
      setReadingType('daily');
      console.log('📋 Starting daily reading (dailyUsed: 0)');
    } else {
      setReadingType('additional');
      console.log('📋 Starting additional reading (dailyUsed:', currentLimits.stats?.dailyUsed, ')');
    }
    
    // Preload new card images for better performance
    preloadImages(newCards.map(card => card.image));
    
    console.log('✅ New reading prepared with', newCards.length, 'cards');
    console.log('🎯 Reading state fully reset - cards should be clickable now');
  };

  // Function to save completed reading (called after animations)
  const saveCompletedReading = async (cards: TarotCardData[], question: string) => {
    log.save('Starting to save completed reading...');
    setIsSaving(true);
    setSaveError(null); // Clear any previous errors
    const deckKeyForSave = cards[0]?.deckKey ?? activeDeck;
    
    // IMMEDIATE UI UPDATES for responsive experience
    log.debug('Updating UI immediately for responsive experience');
    safeSetState(setTotalReadingsToday, prev => prev + 1);
    
    if (readingType === 'daily') {
      safeSetState(setDailyRemaining, prev => Math.max(0, prev - 1));
    } else {
      safeSetState(setAdditionalRemaining, prev => Math.max(0, prev - 1));
    }
    
    try {
      // Save to localStorage with question text
      saveDailyReading(cards, question);
      const newStreak = updateStreak();
      // Local streak will be overridden by database streak after save
      
      log.minimal('Saved to localStorage');

      // Save to Supabase if authenticated
      if (isAuthenticated && user) {
        console.log('🔍 Auth state check:', { isAuthenticated, user: !!user, dbUser: !!dbUser });

        console.log('💾 Saving reading to Supabase:', {
          fid: user.fid,
          cards: cards.map(c => c.id),
          questionText: question,
          readingType,
          deckKey: deckKeyForSave,
        });
        
        // Save reading using new API endpoint with reading type
        console.log('🔍 About to call saveReading with:', {
          user: { fid: user.fid },
          cards: cards.map(c => c.id),
          question,
          readingType,
          deckKey: deckKeyForSave,
        });
        
        // Smart reading type selection with fallback logic
        let optimalReadingType = readingType;
        let useSmartPrediction = false;
        
        console.log('🔍 Debug smart prediction:', {
          hasPreValidatedLimits: !!preValidatedLimits,
          preValidatedLimits,
          currentReadingType: readingType,
          totalReadingsToday
        });
        
        // Check if we have cached limits to make smart decision
        if (preValidatedLimits && preValidatedLimits.stats) {
          const dailyUsed = preValidatedLimits.stats.dailyUsed || 0;
          const canDoDaily = preValidatedLimits.canDoDaily || false;
          const canDoAdditional = preValidatedLimits.canDoAdditional || false;
          
          console.log('📊 Limits analysis:', { dailyUsed, canDoDaily, canDoAdditional });
          
          // If daily already used and we have additional available, skip daily attempt
          if (dailyUsed > 0 && canDoAdditional && readingType === 'daily') {
            console.log('🎯 Smart selection: daily already used, switching to additional immediately');
            optimalReadingType = 'additional';
            setReadingType('additional');
            useSmartPrediction = true;
          } else if (!canDoDaily && canDoAdditional && readingType === 'daily') {
            console.log('🎯 Smart selection: daily not available, switching to additional immediately');
            optimalReadingType = 'additional';
            setReadingType('additional');
            useSmartPrediction = true;
          }
        }
        
        // Fallback: if we're trying daily but have readings today, switch to additional
        if (!useSmartPrediction && readingType === 'daily' && totalReadingsToday > 0) {
          console.log('🔄 Fallback: totalReadingsToday > 0, switching to additional');
          optimalReadingType = 'additional';
          setReadingType('additional');
        }
        
        console.log('💾 Final save decision:', { 
          original: readingType, 
          optimal: optimalReadingType,
          smartPrediction: useSmartPrediction 
        });
        lastSaveAttemptRef.current = {
          cards,
          question,
          readingType: optimalReadingType,
          deckKey: deckKeyForSave,
        };
        
        // Try with fallback logic for robustness
        let saveResult;
        try {
          saveResult = await saveReading(user, cards, question, optimalReadingType, deckKeyForSave);
        } catch (firstError) {
          const errorMessage = firstError instanceof Error ? firstError.message : String(firstError);
          
          // If still daily limit error and we were trying daily, fallback to additional
          if (errorMessage.includes('Daily reading limit reached') && optimalReadingType === 'daily') {
            console.log('🔄 Emergency fallback: daily limit error, retrying with additional');
            setReadingType('additional');
            saveResult = await saveReading(user, cards, question, 'additional', deckKeyForSave);
            console.log('✅ Emergency fallback successful');
          } else {
            throw firstError;
          }
        }
        
        console.log('✅ Reading saved to Supabase successfully:', saveResult);
        lastSaveAttemptRef.current = null;
        
        // Update user streak in database (for all readings)
        console.log('🔥 Updating user streak in database...');
        try {
          const streakResult = await updateUserStreak(user.fid);
          if (streakResult && streakResult.streak) {
            console.log('✅ User streak updated successfully:', streakResult.streak);
            // Update frontend display with database streak count
            safeSetState(setStreakCount, streakResult.streak.count);
            // Update localStorage to keep it in sync
            localStorage.setItem('tarot_streak', streakResult.streak.count.toString());
            console.log('🔥 Frontend streak updated to:', streakResult.streak.count);
          } else {
            console.log('⚠️ Streak update returned null - may need UNIQUE constraint fix');
          }
        } catch (streakError) {
          console.error('❌ STREAK UPDATE FAILED:', streakError);
          console.log('⚠️ Streak update failed - trying direct DB sync fallback...');
          
          // Fallback: try to get streak directly from database
          if (dbUser) {
            try {
              // CRITICAL FIX: Validate FID before using to prevent ReferenceError
              if (!user.fid || isNaN(user.fid) || user.fid <= 0) {
                console.error('❌ FALLBACK STREAK: Invalid FID:', user.fid);
                return;
              }
              
              const { getUserStreak } = await import('./utils/supabaseUtils');
              const fallbackStreak = await getUserStreak(user.fid.toString());
              if (fallbackStreak) {
                safeSetState(setStreakCount, fallbackStreak.count);
                localStorage.setItem('tarot_streak', fallbackStreak.count.toString());
                console.log('🔥 Fallback streak sync successful:', fallbackStreak.count);
              }
            } catch (error) {
              console.warn('⚠️ Fallback streak sync also failed:', error);
            }
          }
        }
        
        // Update states after successful save and streak update (safe)
        safeSetState(setIsSaving, false);
        safeSetState(setIsReadingSaved, true);
        safeSetState(setShowActionButtons, true);
        setSaveError(null); // Clear any previous errors on successful save
        safeSetState(setTotalReadingsToday, prev => prev + 1);
        
        console.log('🎯 State updated after save:', {
          isSaving: false,
          isReadingSaved: true,
          showActionButtons: true,
          totalReadingsToday: totalReadingsToday + 1
        });
        
        // Refresh limits from database for device sync
        setTimeout(async () => {
          if (user) {
            try {
              // Re-fetch limits to sync with database
              const { checkReadingLimits } = await import('./utils/supabaseUtils');
              const updatedLimits = await checkReadingLimits(user.fid);
              
              if (updatedLimits) {
                // Update cached limits for performance
                setPreValidatedLimits(updatedLimits);
                
                safeSetState(setCanDraw, updatedLimits.canDoDaily);
                safeSetState(setCanDoAdditional, updatedLimits.canDoAdditional);
                safeSetState(setDailyRemaining, updatedLimits.stats?.dailyRemaining || 0);
                safeSetState(setAdditionalRemaining, updatedLimits.stats?.additionalRemaining || 0);
                
                const dailyUsed = updatedLimits.stats?.dailyUsed || 0;
                const additionalUsed = updatedLimits.stats?.additionalUsed || 0;
                const newTotal = dailyUsed + additionalUsed;
                safeSetState(setTotalReadingsToday, newTotal);
                
                console.log('🔄 Limits refreshed for device sync:', {
                  ...updatedLimits,
                  newTotal,
                  willShowActionButtons: true
                });
                
                // Ensure action buttons stay visible even when all readings done
                if (newTotal >= 3) {
                  console.log('✅ All readings completed - keeping action buttons visible for sharing');
                }
              }
            } catch (error) {
              console.error('❌ Failed to refresh limits:', error);
              // Keep action buttons visible even if API fails
              console.log('🔄 API failed - keeping action buttons visible for better UX');
            }
          }
        }, 1000);
        
        console.log('🎯 Reading flow completed - showing action buttons');
        
      } else {
        console.log('⚠️ No authenticated user available, skipping Supabase save');
        console.log('📊 Auth details:', { 
          isAuthenticated, 
          hasUser: !!user, 
          userFid: user?.fid,
          hasDbUser: !!dbUser 
        });
        // Ensure UI doesn't stay in saving state when offline/unauthenticated
        safeSetState(setIsSaving, false);
        safeSetState(setIsReadingSaved, true);
        safeSetState(setShowActionButtons, true);
        lastSaveAttemptRef.current = {
          cards,
          question,
          readingType,
          deckKey: deckKeyForSave,
        };
        setSaveError('Reading saved locally — tap "Reconnect & Sync" to try again');
      }
    } catch (error) {
      console.error('❌ Failed to save reading:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : 'No stack trace',
        name: error instanceof Error ? error.name : typeof error
      });
      
      // Always clear saving state to prevent stuck UI (safe)
      safeSetState(setIsSaving, false);
      
      // Set user-friendly error message
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('limit reached')) {
        // Parse the detailed message from API if available
        if (errorMessage.includes('Additional reading limit reached')) {
          setSaveError('All readings completed for today - saved locally');
        } else {
          setSaveError('Limit reached - but reading saved locally');
        }
        console.log('📊 Limit reached - but reading was completed, showing share options');
      } else if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch')) {
        setSaveError('Network error - reading saved locally. Tap "Reconnect & Sync" when online.');
      } else {
        setSaveError('Save failed - reading saved locally. Tap "Reconnect & Sync" to retry.');
      }
      
      // Reading was completed on frontend, just couldn't save to DB
      setIsReadingSaved(true);
      setShowActionButtons(true);
      
      log.debug('Showing buttons despite save failure for better UX');
    }
  };

  const handleRetrySave = async () => {
    if (isSaving || autoRetryInFlightRef.current) return;
    const pending = lastSaveAttemptRef.current;

    if (!pending) {
      setSaveError('No pending reading to sync. Start a new reading.');
      return;
    }

    let activeUser = latestUserRef.current;
    let activeAuth = latestAuthRef.current;

    if ((!activeAuth || !activeUser) && typeof reauthenticate === 'function') {
      const authResult = await reauthenticate();
      activeUser = latestUserRef.current;
      activeAuth = latestAuthRef.current;

      if (!authResult || !activeAuth || !activeUser) {
        setSaveError('Could not sign in. Please try again.');
        return;
      }
    } else if (!activeAuth || !activeUser) {
      setSaveError('Please sign in again to sync with Supabase.');
      return;
    }

    safeSetState(setIsSaving, true);
    setSaveError(null);

    try {
      const deckKeyForSave = pending.deckKey ?? pending.cards[0]?.deckKey ?? activeDeck;
      await saveReading(activeUser, pending.cards, pending.question, pending.readingType, deckKeyForSave);
      await updateUserStreak(activeUser.fid);
      lastSaveAttemptRef.current = null;
      setSaveError(null);
      safeSetState(setIsReadingSaved, true);
      safeSetState(setShowActionButtons, true);
    } catch (error) {
      console.error('Retry save failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);

      if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch')) {
        setSaveError('Network error - still saved locally. Try again when online.');
      } else if (errorMessage.toLowerCase().includes('limit')) {
        setSaveError('Limit reached - could not sync to Supabase.');
      } else {
        setSaveError('Retry failed - reading is still saved locally.');
      }
    } finally {
      safeSetState(setIsSaving, false);
    }
  };

  // Auto-retry when connection returns or auth is already available
  useEffect(() => {
    const tryAutoRetry = () => {
      if (!saveError) return;
      if (!lastSaveAttemptRef.current) return;
      if (isSaving || autoRetryInFlightRef.current) return;
      const online = typeof navigator === 'undefined' ? true : navigator.onLine;
      if (!online) return;
      if (!latestAuthRef.current) return;

      autoRetryInFlightRef.current = true;
      handleRetrySave().finally(() => {
        autoRetryInFlightRef.current = false;
      });
    };

    tryAutoRetry();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', tryAutoRetry);
      return () => {
        window.removeEventListener('online', tryAutoRetry);
      };
    }
  }, [saveError, isSaving]);

  // Manual SDK trigger for debugging (development only)
  // Function to handle additional reading (use new startNewReading logic)
  const handleAdditionalReading = () => {
    // Check if user can actually do more readings
    if (!canDraw && !canDoAdditional) {
      console.log('⚠️ All readings completed for today:', { totalReadingsToday, canDraw, canDoAdditional });
      alert('All readings for today have been completed. Come back tomorrow for more!');
      return;
    }

    if (!canDraw && !canDoAdditional) {
      console.log('⚠️ No readings available according to database limits');
      alert('No more readings available today. Come back tomorrow!');
      return;
    }

    console.log('✅ Starting additional reading...', { 
      totalReadingsToday, 
      canDraw, 
      canDoAdditional 
    });
    startNewReading();
  };

  // Function to reset readings for testing
  const handleResetReadings = async () => {
    handleFanInteract();
    if (isAuthenticated && user) {
      try {
        console.log('🔄 Resetting readings for user:', user.fid);
        await resetUserReadings(user.fid);
        console.log('✅ Readings reset successfully');
        
        // Reset ALL app states safely
        safeSetState(setIsReadingComplete, false);
        safeSetState(setIsReadingSaved, false);
        safeSetState(setShowActionButtons, false);
        safeSetState(setIsSaving, false);
        safeSetState(setSelectedCards, []);
        safeSetState(setSelectedCardModal, null); // Clear any selected card modal
        safeSetState(setQuestionText, '');
        safeSetState(setTotalReadingsToday, 0);
        
        // ADMIN RESET: Don't reset streak to 0, just refresh from database
        console.log('🔄 Admin reset: Refreshing streak from database instead of resetting to 0');
        
        if (isAuthenticated && user && dbUser) {
          try {
            // CRITICAL FIX: Validate FID before using to prevent ReferenceError
            if (!user.fid || isNaN(user.fid) || user.fid <= 0) {
              console.error('❌ RESET STREAK: Invalid FID:', user.fid);
              return;
            }
            
            const { getUserStreak } = await import('./utils/supabaseUtils');
            const currentStreak = await getUserStreak(user.fid.toString());
            if (currentStreak && currentStreak.count !== undefined) {
              safeSetState(setStreakCount, currentStreak.count);
              localStorage.setItem('tarot_streak', currentStreak.count.toString());
              console.log('✅ Admin reset: Streak refreshed from DB:', currentStreak.count);
            } else {
              console.log('⚠️ Admin reset: No streak in DB, setting to 0');
              safeSetState(setStreakCount, 0);
            }
          } catch (error) {
            console.warn('⚠️ Admin reset: Failed to refresh streak from DB:', error);
          }
        }
        
        // Clear localStorage daily reading to allow fresh start
        localStorage.removeItem('tarot_daily_reading');
        
        // Re-fetch fresh limits from database
        const { checkReadingLimits } = await import('./utils/supabaseUtils');
        const updatedLimits = await checkReadingLimits(user.fid);
        
        if (updatedLimits) {
          // After reset, user should be able to draw (force enable)
          setCanDraw(true);
          setCanDoAdditional(updatedLimits.canDoAdditional);
          safeSetState(setDailyRemaining, updatedLimits.stats?.dailyRemaining || 0);
          safeSetState(setAdditionalRemaining, updatedLimits.stats?.additionalRemaining || 0);
          
          const dailyUsed = updatedLimits.stats?.dailyUsed || 0;
          const additionalUsed = updatedLimits.stats?.additionalUsed || 0;
          setTotalReadingsToday(dailyUsed + additionalUsed);
          
          console.log('🔄 Fresh limits after reset:', updatedLimits);
          console.log('🎯 canDraw force-enabled after reset');
        }
        
        // Generate new cards
        const newCards = generateDailyCards(activeDeck);
        setAvailableCards(newCards);
        
        console.log('✅ Complete reset finished - ready for new reading');
        
      } catch (error) {
        console.error('❌ Failed to reset readings:', error);
      }
    }
  };

  const handleManualSDKCall = async () => {
    console.log('🔧 Manual SDK trigger clicked');
    
    try {
      const isMiniApp = await sdk.isInMiniApp();
      console.log('🔍 Is in mini app:', isMiniApp);
      
      if (isMiniApp) {
        await sdk.actions.ready();
        console.log('✅ Manual SDK ready() call successful');
      } else {
        console.log('❌ Not in mini app environment');
      }
    } catch (error) {
      console.error('❌ Manual SDK call failed:', error);
    }
  };

  const deckSwitchDisabled = isSaving || (isReadingComplete && !canDraw && !canDoAdditional);

  // Environment guard and maintenance gating
  if (isMaintenanceMode && !isLoading && !isAdmin) {
    console.log('🚧 Maintenance mode activated for non-admin user');
    return <MaintenanceModal isOpen={true} />;
  }

  if (farcasterAccess === 'unknown') {
    return (
      <Background>
        <div className="min-h-screen flex flex-col items-center justify-center text-white">
          <div className="text-lg font-medium">Checking STRADAMUS environment…</div>
        </div>
      </Background>
    );
  }

  if (farcasterAccess === 'blocked') {
    console.log('🚫 STRADAMUS opened outside Farcaster mini app');
    return (
      <Background>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center text-white">
          <div className="max-w-md space-y-6">
            <div className="bg-white/10 border border-white/15 rounded-2xl px-6 py-6 backdrop-blur-sm shadow-lg">
              <h2 className="crypto-heading text-2xl mb-3">Open STRADAMUS inside Farcaster</h2>
              <p className="text-white/70 text-sm leading-relaxed">
                This mini app only runs inside the Farcaster client. Launch it from the Mini Apps tab or use the button below.
              </p>
            </div>
            <a
              href={FARCASTER_DEEP_LINK}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-purple-900/40 font-medium"
            >
              <Layers className="w-5 h-5" />
              <span>Open STRADAMUS in Farcaster</span>
            </a>
            <p className="text-white/50 text-xs leading-relaxed">
              Tip: add STRADAMUS to your favorites to jump back in with one tap.
            </p>
          </div>
        </div>
      </Background>
    );
  }

  if (isLoading) {
    return (
      <Background>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-white text-xl">Loading your daily cards...</div>
        </div>
      </Background>
    );
  }

  return (
    <Background>
      <div className="relative z-10 min-h-screen overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sm:py-6 relative z-20 safe-top">
          {/* Streak - moved to left */}
          <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5">
            <Flame className={`w-4 h-4 ${streakCount > 0 ? 'text-orange-400' : 'text-white'}`} />
            <span className="text-sm font-medium text-white">{streakCount}</span>
          </div>
          
          <div className="text-center">
            <h1 className="crypto-heading text-white text-xl mb-1 sm:mb-2"
                onClick={() => {
                  const now = Date.now();
                  const lastTap = (window as any).lastDebugTap || 0;
                  if (now - lastTap < 500) {
                    const tapCount = ((window as any).debugTapCount || 0) + 1;
                    (window as any).debugTapCount = tapCount;
                    if (tapCount >= 3) {
                      setShowDebugPanel(!showDebugPanel);
                      (window as any).debugTapCount = 0;
                    }
                  } else {
                    (window as any).debugTapCount = 1;
                  }
                  (window as any).lastDebugTap = now;
                }}
            >
              STRADAMUS
            </h1>
            {selectedCards.length === 0 && !isReadingComplete && (
              <p className="text-white/70 text-xs sm:text-sm font-medium tracking-wide">
                {canDraw || canDoAdditional
                  ? "Discover your daily crypto forecast"
                  : "You've already drawn your cards today"
                }
              </p>
            )}
          </div>
          
          <div className="flex items-center space-x-[6px]">
            {enableStandardDeck && (
              <DeckToggle
                value={activeDeck}
                onChange={handleSelectDeck}
                disabled={deckSwitchDisabled}
              />
            )}

            {/* Burger Menu */}
            <button
              onClick={() => setShowMenu(true)}
              className="flex items-center justify-center px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-colors"
              title="Menu"
            >
              <MenuIcon className="w-5 h-5 text-white" />
            </button>
            
            {/* Test Panel Button - Only in development */}
          </div>
        </div>

        {/* ENHANCED Debug info for device sync troubleshooting */}
        {import.meta.env.DEV && isAuthenticated && user && (
          <div className="px-4 mb-2 bg-black/50 rounded text-xs text-white/70">
            <div>👤 FID: {user.fid} | User: {user.username} | DB: {dbUser ? '✅' : '❌'}</div>
            <div>📋 Today: {totalReadingsToday}/3 | Can Draw: {canDraw ? '✅' : '❌'} | Can Add: {canDoAdditional ? '✅' : '❌'}</div>
            <div>🔥 Streak: {streakCount} | Complete: {isReadingComplete ? '✅' : '❌'} | Type: {readingType}</div>
            <div>💾 Saved: {isReadingSaved ? '✅' : '❌'} | Cards: {selectedCards.length} | Loading: {isLoading ? '✅' : '❌'}</div>
            <div>🎯 Actions: {showActionButtons ? '✅' : '❌'} | Saving: {isSaving ? '✅' : '❌'}</div>
          </div>
        )}

        {/* Selected Cards Area */}
        <div className="px-4 mb-4 pb-64"> {/* Reduced pb-72 to pb-64 */}
          <div className="relative">
            {/* PROTECTED: SelectedCards with error boundary */}
            {(() => {
              try {
                if (!selectedCards || !Array.isArray(selectedCards)) {
                  console.error('❌ SelectedCards: Invalid selectedCards:', selectedCards);
                  return <div className="text-center p-2 text-red-400">Error with selected cards</div>;
                }
                
                return (
                  <SelectedCards 
                    selectedCards={selectedCards}
                    maxCards={maxCards}
                    onCardClick={handleSelectedCardClick}
                    onEmptySlotTap={handleEmptySlotTap}
                  />
                );
              } catch (error) {
                console.error('❌ SelectedCards render error:', error);
                return <div className="text-center p-2 text-red-400">Error displaying cards</div>;
              }
            })()}
            {showFanHint && (
              <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                <div className="px-4 py-1.5 rounded-full border border-white/20 bg-black/80 text-[11px] text-white font-semibold tracking-wide shadow-md whitespace-nowrap">
                  Tap the fan below to draw
                </div>
                <div
                  className="mt-1"
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid rgba(0,0,0,0.8)',
                  }}
                />
              </div>
            )}
          </div>

          {/* Cards Left Indicator - PROTECTED */}
          <div className="text-center mt-2 mb-2"> {/* Reduced margins */}
            <div className="text-purple-300 text-lg font-medium tracking-wide">
              {(() => {
                try {
                  const safeCardsLeft = typeof cardsLeft === 'number' && !isNaN(cardsLeft) ? cardsLeft : 0;
                  return safeCardsLeft > 0 
                    ? `${safeCardsLeft} card${safeCardsLeft === 1 ? '' : 's'} left` 
                    : "Reading complete";
                } catch (error) {
                  console.error('❌ Cards left calculation error:', error);
                  return "Cards remaining: --";
                }
              })()}
            </div>
          </div>

          {/* Question Field - FIXED: Show during any active reading (daily OR additional) - PROTECTED */}
          {(() => {
            try {
              const safeSelectedCards = selectedCards || [];
              const safeMaxCards = typeof maxCards === 'number' ? maxCards : 3;
              const safeTotalReadings = typeof totalReadingsToday === 'number' ? totalReadingsToday : 0;
              
              return safeSelectedCards.length < safeMaxCards && (canDraw || canDoAdditional) && safeTotalReadings < 3;
            } catch (error) {
              console.error('❌ Question field condition error:', error);
              return false;
            }
          })() && (
            <QuestionField
              value={questionText}
              onChange={setQuestionText}
              maxLength={maxQuestionLength}
            />
          )}



          {/* Saving Message */}
          {isSaving && (
            <div className="flex justify-center mt-4">
              <div className="flex items-center space-x-2 text-purple-300">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-300"></div>
                <span className="text-sm">Saving reading...</span>
              </div>
            </div>
          )}

          {/* Save Error - Simplified (Retry Button Disabled) */}
          {saveError && showActionButtons && (
            <div className="flex flex-col items-center gap-2 mt-4 px-4">
              <div className="bg-orange-500/20 border border-orange-400/20 rounded-lg p-3 max-w-md">
                <p className="text-orange-300 text-sm text-center">{saveError}</p>
                {!saveError.toLowerCase().includes('limit') && (
                  <button
                    onClick={handleRetrySave}
                    disabled={isSaving}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Syncing...
                      </>
                    ) : (
                      'Reconnect & Sync'
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons - shown only after reading is saved */}
          {showActionButtons && isReadingSaved && (
            <div className="flex flex-col items-center gap-3 mt-4 relative z-30">
              {interpretationFeatureEnabled && (
                <button
                  onClick={handleOpenInterpretationModal}
                  disabled={interpretationState === 'processing' || remainingInterpretations <= 0}
                  className={`w-64 flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 px-6 py-3 rounded-full transition-all duration-200 transform hover:scale-105 relative z-30 text-white font-medium ${
                    interpretationState === 'processing' || remainingInterpretations <= 0 ? 'opacity-60 pointer-events-none' : ''
                  }`}
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="tracking-wide">{interpretationLabel}</span>
                </button>
              )}

              {/* Share Button - always shown after save */}
              <button
                onClick={handleShare}
                className="w-64 flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-full transition-all duration-200 transform hover:scale-105 relative z-30 text-white"
              >
                <ShareArrowIcon className="w-5 h-5" />
                <span className="font-medium tracking-wide">Share Reading</span>
              </button>

              {/* Next Reading Button - FIXED: Show accurate remaining count */}
              {(canDraw || canDoAdditional) && totalReadingsToday < 3 && (
                <button
                  onClick={handleAdditionalReading}
                  className="w-64 flex items-center justify-center space-x-2 bg-transparent border-2 border-purple-500 hover:border-purple-400 px-6 py-3 rounded-full transition-all duration-200 transform hover:scale-105 relative z-30 text-purple-300 hover:text-purple-200"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-medium tracking-wide">
                    Next Reading ({(dailyRemaining + additionalRemaining) || 0} left)
                  </span>
                </button>
              )}
            </div>
          )}

          {/* 🎯 UI LOGIC FIX: Show completion message ONLY when ALL readings are exhausted
              PREVIOUS BUG: Was showing after EVERY reading completion  
              WHY COMPLEX: Must check both daily + additional limits are truly zero */}
          {((dailyRemaining + additionalRemaining) <= 0 && !canDraw && !canDoAdditional) && (
            <div className="flex justify-center mt-4">
              <p className="text-white/70 text-center px-4 text-sm font-medium">
                All readings complete! Share your insights and return tomorrow for more guidance.
              </p>
            </div>
          )}

        </div>

        {/* Card Fan - PROTECTED: Critical error boundary */}
        {(() => {
          try {
            // Validate critical props before rendering
            if (!availableCards || !Array.isArray(availableCards)) {
              console.error('❌ CardFan: Invalid availableCards:', availableCards);
              return <div className="text-center p-4 text-red-400">Error loading cards</div>;
            }
            
            if (!selectedCards || !Array.isArray(selectedCards)) {
              console.error('❌ CardFan: Invalid selectedCards:', selectedCards);
              return <div className="text-center p-4 text-red-400">Error with selected cards</div>;
            }
            
            return (
              <CardFan 
                cards={availableCards}
                selectedCards={selectedCards}
                onCardSelect={handleCardSelect}
                disabled={(!canDraw && !canDoAdditional) || cardsLeft === 0}
                onFanInteract={handleFanInteract}
              />
            );
          } catch (error) {
            console.error('❌ CardFan render error:', error);
            return <div className="text-center p-4 text-red-400">Error rendering cards</div>;
          }
        })()}

        {/* Card Modal */}
        {selectedCardModal && (
          <CardModal
            card={selectedCardModal}
            onClose={() => setSelectedCardModal(null)}
          />
        )}

        {/* Share Modal */}
        {shareModalOpen && (
          <ShareModal
            selectedCards={selectedCards}
            onClose={() => {
              setShareModalOpen(false);
              setShareUrlOverride(undefined);
            }}
            mode={shareContext}
            insightText={shareInsightText}
            insightLabel={interpretationLabel}
            shareUrlOverride={shareUrlOverride}
          />
        )}

        {/* Debug Panel for mobile debugging */}
        {showDebugPanel && (
          <div className="fixed top-2 left-2 z-50 bg-red-900/90 text-white text-xs p-2 rounded max-w-xs">
            <div className="font-bold mb-1">DEBUG:</div>
            <div style={{ fontSize: '10px', lineHeight: '12px' }}>
              {debugInfo || 'Debug panel active'}
              <br />Auth: {isAuthenticated ? 'Yes' : 'No'}
              <br />FID: {user?.fid || 'None'}
              <br />Host: {isBaseApp ? 'Base' : miniAppReferrer || 'Unknown'}
              <br />Readings: {totalReadingsToday}
              <br />CanDraw: {canDraw ? 'Yes' : 'No'}
              <br />
              <button 
                onClick={() => {
                  const current = localStorage.getItem('debug_mode') === 'true';
                  localStorage.setItem('debug_mode', (!current).toString());
                  window.location.reload();
                }}
                className="text-xs bg-white/20 px-2 py-1 rounded mt-1"
              >
                Debug: {localStorage.getItem('debug_mode') === 'true' ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
        )}

        {/* Test Panel */}
        {import.meta.env.DEV && testPanelOpen && (
          <TestPanel
            onClose={() => setTestPanelOpen(false)}
          />
        )}

        {USE_LEGACY_ONBOARDING && showOnboarding && (
          <OnboardingModal
            onClose={() => setShowOnboarding(false)}
            onComplete={() => {
              console.log('Onboarding completed');
            }}
            userFid={user?.fid}
          />
        )}

        {/* Admin Settings Modal */}
        {showAdminSettings && isAdmin && (
          <AdminSettingsModal
            isOpen={showAdminSettings}
            onClose={() => setShowAdminSettings(false)}
            userFid={user?.fid?.toString() || ''}
          />
        )}

        {/* Menu Modal */}
        <MenuModal
          isOpen={showMenu}
          onClose={() => setShowMenu(false)}
          onRequestAddMiniApp={handleManualAddMiniApp}
          activeDeck={activeDeck}
          onSelectDeck={handleSelectDeck}
          deckSwitchDisabled={deckSwitchDisabled}
          standardDeckEnabled={enableStandardDeck}
          onSelectInfo={() => setShowInfo(true)}
          onSelectHistory={() => setShowHistory(true)}
        />
        <InfoModal
          isOpen={showInfo}
          onClose={() => setShowInfo(false)}
        />
        <ReadingHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          readings={readingHistory}
          isLoading={readingHistoryLoading}
          onRequestInsight={handleHistoryRequestInsight}
          onShare={handleHistoryShare}
        />
        {interpretationFeatureEnabled && (
          <>
            <InterpretationModal
              isOpen={interpretationState === 'modal' || interpretationState === 'processing'}
              onClose={handleCloseInterpretationModal}
              onConfirm={handleConfirmInterpretation}
              title={interpretationLabel}
              priceLabel={priceLabel}
              isProcessing={interpretationState === 'processing'}
            />
            {interpretationState === 'ready' && !shareModalOpen && (
              <InterpretationResultModal
                isOpen
                interpretation={interpretationText}
                onCopy={handleCopyInterpretation}
                onShare={handleShareInterpretation}
                onRequestNew={handleRequestAnotherInterpretation}
                canRequestAnother={remainingInterpretations > 0}
                priceLabel={priceLabel}
                onClose={handleCloseInterpretationResult}
                title={interpretationLabel}
              />
            )}
          </>
        )}
      </div>
    </Background>
  );
}

export default App;
